import React, { useState, useEffect, useCallback } from "react";
import api, { formatVND, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Settings, MapPin, Users, Plus, Pencil, Trash2, Bell, AlertTriangle, Save, Eye, EyeOff, Wifi, Loader2, Power, Wrench, Clock, DollarSign, TrendingUp, Calendar, Activity, Zap, Send, Upload, FileUp, Trophy, History, HardDrive, Download, Play, Shield, Code, RefreshCw, Copy, KeyRound, Link as LinkIcon, QrCode, Link2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import QRCodeManager from "@/components/settings/QRCodeManager";
import ShortLinkManager from "@/components/settings/ShortLinkManager";

export default function SettingsPage() {
  const { user: currentUser } = useAuth();
  const isCeo = currentUser?.role === "ceo";
  const [courts, setCourts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingCourts, setLoadingCourts] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // User dialog
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", phone: "", role: "staff" });
  const [userError, setUserError] = useState("");
  const [userSubmitting, setUserSubmitting] = useState(false);

  // Court edit dialog
  const [courtDialogOpen, setCourtDialogOpen] = useState(false);
  const [editingCourt, setEditingCourt] = useState(null);
  const [courtError, setCourtError] = useState("");
  const [courtSubmitting, setCourtSubmitting] = useState(false);
  const [occupancyData, setOccupancyData] = useState({});
  const [togglingCourt, setTogglingCourt] = useState(null);

  // User edit/logs
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editUserData, setEditUserData] = useState({ name: "", email: "", phone: "", role: "staff" });
  const [editUserSaving, setEditUserSaving] = useState(false);
  const [logUser, setLogUser] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);


  // Notification settings
  const [notiSettings, setNotiSettings] = useState({
    zalo_oa_id: "", zalo_api_key: "", zalo_secret_key: "",
    sms_api_key: "", sms_provider: "esms",
    auto_remind_expiring: true, remind_days_before: 7,
    zns_template_nhac_gia_han: "", zns_template_xac_nhan_dat_san: "", zns_template_khuyen_mai: "",
    zalo_connected: false, zalo_configured: false,
    zalo_cred_oa_id: "", zalo_cred_app_id: "",
    zalo_cred_app_secret_length: 0,
    zalo_cred_app_secret_preview: "",
    zalo_access_token: "", zalo_refresh_token: "",
    // Editable cred fields (chỉ điền khi paste mới — backend dùng strip())
    zalo_app_id_input: "",
    zalo_app_secret_input: "",
    zalo_oa_id_input: "",
    // Lệ phí giải đấu
    zns_template_le_phi: "",
    // Đặt sân online
    zns_template_dat_san_cho_xn: "",
    zns_template_dat_san_xac_nhan: "",
    zns_template_le_phi_giai: "",
    zns_sdt_nhan_thongbao: "",
    tournament_content: "",
    tournament_fee: "",
    tournament_bank_account: "",
    tournament_deadline: "",
  });
  const [notiLoading, setNotiLoading] = useState(false);
  const [notiSaving, setNotiSaving] = useState(false);
  const [showKeys, setShowKeys] = useState({});
  const [zaloTesting, setZaloTesting] = useState(false);
  const [zaloTestResult, setZaloTestResult] = useState(null);
  // Force refresh + Test ZNS state
  const [zaloRefreshing, setZaloRefreshing] = useState(false);
  const [zaloTestZnsOpen, setZaloTestZnsOpen] = useState(false);
  const [zaloTestPhone, setZaloTestPhone] = useState("");
  const [zaloTestZnsSending, setZaloTestZnsSending] = useState(false);
  const [zaloTestZnsResult, setZaloTestZnsResult] = useState(null);
  // Diagnose dialog
  const [zaloDiagnoseOpen, setZaloDiagnoseOpen] = useState(false);
  // ZNS Proxy API (cho hệ thống bên ngoài dùng chung Zalo creds)
  const [znsProxyConfig, setZnsProxyConfig] = useState(null);
  const [znsProxyKeyVisible, setZnsProxyKeyVisible] = useState(false);
  const [znsProxyRegenerating, setZnsProxyRegenerating] = useState(false);
  const [znsProxyExampleOpen, setZnsProxyExampleOpen] = useState(false);
  // ZNS Proxy logs
  const [znsProxyLogs, setZnsProxyLogs] = useState(null);
  const [znsProxyLogsLoading, setZnsProxyLogsLoading] = useState(false);
  const [znsProxyLogsFilter, setZnsProxyLogsFilter] = useState("all"); // all | success | fail
  const [znsProxyLogsExpanded, setZnsProxyLogsExpanded] = useState({}); // expand raw per entry idx
  const [zaloDiagnosing, setZaloDiagnosing] = useState(false);
  const [zaloDiagnoseResult, setZaloDiagnoseResult] = useState(null);
  const [zaloRefreshHistory, setZaloRefreshHistory] = useState(null);
  // Reset circuit breaker
  const [zaloResetting, setZaloResetting] = useState(false);
  // Health probe (synthetic monitoring)
  const [zaloProbing, setZaloProbing] = useState(false);
  const [zaloProbeStatus, setZaloProbeStatus] = useState(null);

  // Tournament fee send
  const [tournamentPhones, setTournamentPhones] = useState("");
  const [tournamentSending, setTournamentSending] = useState(false);
  const [tournamentResult, setTournamentResult] = useState(null);

  // Tournament ZNS configs
  const [tournaments, setTournaments] = useState([]);
  const [tournZnsConfigs, setTournZnsConfigs] = useState({});
  const [tournZnsSaving, setTournZnsSaving] = useState("");

  useEffect(() => {
    loadCourts();
    loadUsers();
    loadNotiSettings();
    loadTournaments();
  }, []);

  const loadOccupancy = useCallback(async () => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const { data } = await api.get(`/courts/occupancy-all?date=${today}`);
      const map = {};
      (data.occupancy || []).forEach((o) => { map[o.court_id] = o; });
      setOccupancyData(map);
    } catch (err) {
      console.error("Failed to load occupancy", err);
    }
  }, []);

  useEffect(() => {
    loadOccupancy();
  }, [loadOccupancy]);

  const loadCourts = async () => {
    setLoadingCourts(true);
    try {
      const { data } = await api.get("/courts");
      setCourts(data.courts || []);
    } catch (err) {
      console.error("Failed to load courts", err);
    } finally {
      setLoadingCourts(false);
    }
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data.users || []);
    } catch (err) {
      console.error("Failed to load users", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadNotiSettings = async () => {
    setNotiLoading(true);
    try {
      const { data } = await api.get("/notifications/settings");
      if (data.settings) {
        // Quan trọng: KHÔNG ghi đè token user đang gõ vở (nếu UI dirty) khi auto-refetch.
        // Ở đây dùng full replace vì page mount + manual reload mới gọi hàm này.
        setNotiSettings(data.settings);
      }
    } catch (err) {
      // CEO-only endpoint, silently fail for non-CEO
    } finally {
      setNotiLoading(false);
    }
  };

  // Auto-refresh các field MONITORING (không đè token user đang gõ): expires_at,
  // last_refresh_at, last_refresh_error, zalo_connected — pull từ DB mỗi 20s
  // để UI luôn phản ánh đúng trạng thái background auto-refresh token.
  // Token preview hiển thị riêng ở monitoring panel (đọc realtime từ DB),
  // các input editable KHÔNG bị ghi đè khi user đang gõ.
  useEffect(() => {
    let timer;
    const tick = async () => {
      try {
        const { data } = await api.get("/notifications/settings");
        const s = data?.settings;
        if (!s) return;
        setNotiSettings(prev => ({
          ...prev,
          zalo_connected: s.zalo_connected,
          zalo_configured: s.zalo_configured,
          zalo_cred_oa_id: s.zalo_cred_oa_id,
          zalo_cred_app_id: s.zalo_cred_app_id,
          zalo_cred_app_secret_length: s.zalo_cred_app_secret_length,
          zalo_cred_app_secret_preview: s.zalo_cred_app_secret_preview,
          zalo_expires_at: s.zalo_expires_at,
          zalo_last_refresh_at: s.zalo_last_refresh_at,
          zalo_last_refresh_error: s.zalo_last_refresh_error,
          zalo_updated_at: s.zalo_updated_at,
          zalo_circuit_state: s.zalo_circuit_state,
          zalo_alert_discord_channel_id: s.zalo_alert_discord_channel_id,
          zalo_probe_phone: s.zalo_probe_phone,
          zalo_probe_enabled: s.zalo_probe_enabled,
          zalo_probe_state: s.zalo_probe_state,
          zalo_probe_interval_hours: s.zalo_probe_interval_hours,
          zalo_probe_max_per_day: s.zalo_probe_max_per_day,
          // Token preview live (read-only, không ảnh hưởng input editable)
          zalo_db_access_token: s.zalo_access_token || "",
          zalo_db_refresh_token: s.zalo_refresh_token || "",
        }));
      } catch (e) {
        // ignore
      }
    };
    timer = setInterval(tick, 20000);
    // Load probe status on mount
    loadProbeStatus();
    // Load ZNS proxy config + logs on mount
    loadZnsProxyConfig();
    loadZnsProxyLogs("all");
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleZaloRefreshNow = async () => {
    setZaloRefreshing(true);
    try {
      const { data } = await api.post("/notifications/zalo/refresh-now");
      if (data.success) {
        toast.success(data.message || "Refresh token thành công!");
        await loadNotiSettings();
      } else {
        toast.error(data.message || "Refresh thất bại");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi gọi refresh");
    } finally {
      setZaloRefreshing(false);
    }
  };

  const handleZaloDiagnose = async () => {
    setZaloDiagnosing(true);
    setZaloDiagnoseResult(null);
    setZaloRefreshHistory(null);
    setZaloDiagnoseOpen(true);
    try {
      // Gọi song song: live diagnose + lấy history persisted
      const [diagRes, histRes] = await Promise.allSettled([
        api.post("/notifications/zalo/diagnose"),
        api.get("/notifications/zalo/refresh-history"),
      ]);
      if (diagRes.status === "fulfilled") {
        setZaloDiagnoseResult(diagRes.value.data);
      } else {
        setZaloDiagnoseResult({
          ok: false,
          interpretation: formatApiError(diagRes.reason?.response?.data?.detail) || "Lỗi gọi diagnose",
        });
      }
      if (histRes.status === "fulfilled") {
        setZaloRefreshHistory(histRes.value.data);
      }
    } finally {
      setZaloDiagnosing(false);
    }
  };

  const handleZaloResetCircuit = async () => {
    if (!window.confirm("Reset circuit breaker? Hệ thống sẽ thử refresh lại ngay khi có request kế tiếp.")) return;
    setZaloResetting(true);
    try {
      const { data } = await api.post("/notifications/zalo/reset-circuit");
      if (data.success) {
        toast.success(data.message || "Đã reset circuit breaker");
        await loadNotiSettings();
      } else {
        toast.error("Reset thất bại");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi reset");
    } finally {
      setZaloResetting(false);
    }
  };

  const loadProbeStatus = async () => {
    try {
      const { data } = await api.get("/notifications/zalo/probe-status");
      setZaloProbeStatus(data);
    } catch (e) { /* ignore */ }
  };

  const loadZnsProxyConfig = async () => {
    try {
      const { data } = await api.get("/zns/proxy/config");
      setZnsProxyConfig(data);
    } catch (e) { /* ignore for non-CEO */ }
  };

  const loadZnsProxyLogs = async (filter = znsProxyLogsFilter) => {
    setZnsProxyLogsLoading(true);
    try {
      const { data } = await api.get(`/zns/proxy/logs?limit=50&status=${filter}`);
      setZnsProxyLogs(data);
    } catch (e) { /* ignore */ } finally {
      setZnsProxyLogsLoading(false);
    }
  };

  const handleRegenerateProxyKey = async () => {
    if (!window.confirm(
      "Tạo lại API Key sẽ làm key cũ KHÔNG còn dùng được. Mọi hệ thống đang gọi với key cũ sẽ ngừng hoạt động.\n\nBạn chắc chắn?"
    )) return;
    setZnsProxyRegenerating(true);
    try {
      const { data } = await api.post("/zns/proxy/regenerate-key");
      if (data.ok) {
        toast.success("Đã tạo API Key mới. Hãy cập nhật key này cho các hệ thống đang sử dụng.");
        await loadZnsProxyConfig();
        setZnsProxyKeyVisible(true);  // show ngay key mới
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi tạo lại key");
    } finally {
      setZnsProxyRegenerating(false);
    }
  };

  const handleToggleProxyEnabled = async (next) => {
    try {
      await api.put("/zns/proxy/config", { enabled: next });
      toast.success(next ? "Đã bật ZNS Proxy" : "Đã tắt ZNS Proxy");
      await loadZnsProxyConfig();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi cập nhật proxy");
    }
  };

  const handleCopy = async (text, label = "Đã sao chép") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      // fallback: select & copy via input
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success(label); } catch { toast.error("Không sao chép được"); }
      document.body.removeChild(ta);
    }
  };

  const handleZaloProbeNow = async () => {
    setZaloProbing(true);
    try {
      const { data } = await api.post("/notifications/zalo/probe-now");
      if (data.success) {
        toast.success(`Probe OK — đã gửi ZNS test tới ${data.phone}`);
      } else {
        toast.error(`Probe FAIL: ${data.error_summary || "Unknown error"}`);
      }
      await loadProbeStatus();
      await loadNotiSettings();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi probe");
    } finally {
      setZaloProbing(false);
    }
  };

  const handleZaloTestZns = async () => {
    if (!zaloTestPhone || zaloTestPhone.replace(/\D/g, "").length < 9) {
      toast.error("Vui lòng nhập SĐT hợp lệ");
      return;
    }
    setZaloTestZnsSending(true);
    setZaloTestZnsResult(null);
    try {
      const { data } = await api.post("/notifications/zalo/test-zns", { phone: zaloTestPhone });
      setZaloTestZnsResult(data);
      if (data.success) {
        toast.success("Đã gửi ZNS test, vui lòng kiểm tra SĐT");
      } else {
        toast.error(data.message || "Gửi ZNS thất bại");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi gọi test ZNS");
    } finally {
      setZaloTestZnsSending(false);
    }
  };

  const handleSaveNotiSettings = async () => {
    setNotiSaving(true);
    try {
      // Build payload + map editable cred fields → backend keys
      const payload = {
        ...notiSettings,
        zalo_app_id: notiSettings.zalo_app_id_input?.trim() || "",
        zalo_app_secret: notiSettings.zalo_app_secret_input?.trim() || "",
        zalo_oa_id_cred: notiSettings.zalo_oa_id_input?.trim() || "",
      };
      // Validate App ID pattern: phải 17-18 chữ số nguyên (Zalo standard).
      // Bug case 113.10: app_id thừa 1 số `2` (19 chars) → -14002 Invalid appId.
      if (payload.zalo_app_id && !/^\d{17,18}$/.test(payload.zalo_app_id)) {
        toast.error(
          `App ID không đúng định dạng: ${payload.zalo_app_id} (${payload.zalo_app_id.length} ký tự). ` +
          `App ID Zalo phải là 17-18 chữ số nguyên (chỉ chứa 0-9). Vui lòng kiểm tra lại từ Zalo Console.`
        );
        setNotiSaving(false);
        return;
      }
      // Warn nếu secret bị paste nhầm (quá dài)
      const secLen = payload.zalo_app_secret.length;
      if (secLen > 100) {
        if (!window.confirm(
          `App Secret bạn paste dài ${secLen} ký tự — quá dài so với chuẩn Zalo (~32 ký tự). ` +
          `Có thể bạn dán nhầm refresh_token vào field này. Tiếp tục lưu?`
        )) {
          setNotiSaving(false);
          return;
        }
      }
      await api.put("/notifications/settings", payload);
      toast.success("Đã lưu cấu hình thông báo. Circuit breaker đã reset, hệ thống sẽ thử refresh ngay.");
      // Clear input fields sau khi lưu thành công + reload
      setNotiSettings(s => ({
        ...s,
        zalo_app_id_input: "", zalo_app_secret_input: "", zalo_oa_id_input: "",
        zalo_access_token: "", zalo_refresh_token: "",
      }));
      await loadNotiSettings();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi khi lưu cấu hình");
    } finally {
      setNotiSaving(false);
    }
  };

  const loadTournaments = async () => {
    try {
      const { data } = await api.get("/tournaments");
      setTournaments(data.tournaments || []);
      const cfgs = {};
      for (const t of (data.tournaments || [])) {
        cfgs[t.id] = { zns_template_id: t.zns_template_id || "" };
      }
      setTournZnsConfigs(cfgs);
    } catch { /* ignore for non-CEO */ }
  };

  const handleSaveTournZns = async (tournId) => {
    setTournZnsSaving(tournId);
    try {
      const val = tournZnsConfigs[tournId]?.zns_template_id || "";
      await api.put(`/tournaments/${tournId}`, { zns_template_id: val });
      toast.success("Đã lưu cấu hình ZNS giải đấu");
    } catch (err) {
      toast.error("Lỗi lưu cấu hình");
    } finally {
      setTournZnsSaving("");
    }
  };


  const toggleShowKey = (field) => {
    setShowKeys(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleImportPhones = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result || "";
      // Extract phone numbers from CSV/TXT - one per line or comma-separated
      const phones = text.replace(/[,;\t]/g, "\n").split("\n").map(s => s.trim()).filter(s => /^0\d{8,10}$/.test(s));
      if (phones.length > 0) {
        setTournamentPhones(phones.join("\n"));
        toast.success(`Đã import ${phones.length} số điện thoại`);
      } else {
        toast.error("Không tìm thấy số điện thoại hợp lệ trong file");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleSendTournamentFee = async () => {
    const phones = tournamentPhones.split("\n").map(s => s.trim()).filter(Boolean);
    if (phones.length === 0) { toast.error("Chưa có số điện thoại"); return; }
    if (!notiSettings.tournament_content) { toast.error("Chưa nhập nội dung thi đấu (X)"); return; }
    if (!notiSettings.tournament_fee) { toast.error("Chưa nhập lệ phí (Y)"); return; }
    setTournamentSending(true);
    setTournamentResult(null);
    try {
      const { data } = await api.post("/notifications/send-tournament-fee", {
        phones,
        tournament_content: notiSettings.tournament_content,
        tournament_fee: notiSettings.tournament_fee,
        bank_account: notiSettings.tournament_bank_account,
        deadline: notiSettings.tournament_deadline,
        zns_template_id: notiSettings.zns_template_le_phi,
      });
      setTournamentResult(data);
      toast.success(data.message || `Đã gửi ${data.sent || 0}/${phones.length}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi gửi thông báo");
    } finally {
      setTournamentSending(false);
    }
  };

  const handleTestZaloConnection = async () => {
    setZaloTesting(true);
    setZaloTestResult(null);
    try {
      const { data } = await api.get("/notifications/zalo/test-connection");
      setZaloTestResult(data);
      if (data.success) {
        toast.success("Kết nối Zalo OA thành công!");
        loadNotiSettings();
      } else {
        toast.error(data.message || "Lỗi kết nối Zalo");
      }
    } catch (err) {
      setZaloTestResult({ success: false, message: "Lỗi kết nối" });
      toast.error("Lỗi kết nối Zalo");
    } finally {
      setZaloTesting(false);
    }
  };

  const handleCreateUser = async () => {
    setUserError("");
    setUserSubmitting(true);
    try {
      await api.post("/users", newUser);
      setUserDialogOpen(false);
      setNewUser({ name: "", email: "", password: "", phone: "", role: "staff" });
      loadUsers();
    } catch (err) {
      setUserError(formatApiError(err.response?.data?.detail));
    } finally {
      setUserSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa người dùng này?")) return;
    try {
      await api.delete(`/users/${userId}`);
      loadUsers();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const openEditUser = (u) => {
    setEditUser(u);
    setEditUserData({ name: u.name, email: u.email, phone: u.phone || "", role: u.role });
    setEditUserOpen(true);
  };

  const handleEditUser = async () => {
    if (!editUser) return;
    setEditUserSaving(true);
    try {
      await api.put(`/users/${editUser.id}`, editUserData);
      toast.success("Đã cập nhật");
      setEditUserOpen(false);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi cập nhật");
    } finally { setEditUserSaving(false); }
  };

  const openUserLogs = async (u) => {
    setLogUser(u);
    setLogOpen(true);
    setLogsLoading(true);
    try {
      const { data } = await api.get(`/users/${u.id}/logs`);
      setActivityLogs(data.logs || []);
    } catch { setActivityLogs([]); }
    finally { setLogsLoading(false); }
  };



  const openCourtEdit = (court) => {
    // Compute base/peak/weekend from pricing if not already set
    const weekday = court.pricing?.find((p) => p.day_type === "weekday");
    const weekend = court.pricing?.find((p) => p.day_type === "weekend");
    const minWeekday = weekday?.time_slots?.length ? Math.min(...weekday.time_slots.map(s => s.price)) : 0;
    const maxWeekday = weekday?.time_slots?.length ? Math.max(...weekday.time_slots.map(s => s.price)) : 0;
    const maxWeekend = weekend?.time_slots?.length ? Math.max(...weekend.time_slots.map(s => s.price)) : 0;
    setEditingCourt({
      ...JSON.parse(JSON.stringify(court)),
      base_price: court.base_price || minWeekday,
      peak_price: court.peak_price || maxWeekday,
      weekend_price: court.weekend_price || maxWeekend,
      description: court.description || "",
    });
    setCourtError("");
    setCourtDialogOpen(true);
  };

  const handleUpdateCourt = async () => {
    if (!editingCourt) return;
    // Price validation
    if (editingCourt.peak_price < editingCourt.base_price) {
      setCourtError("Giá cao điểm phải >= giá cơ bản");
      return;
    }
    if (editingCourt.weekend_price < editingCourt.base_price) {
      setCourtError("Giá cuối tuần phải >= giá cơ bản");
      return;
    }
    setCourtError("");
    setCourtSubmitting(true);
    try {
      await api.put(`/courts/${editingCourt.id}`, {
        name: editingCourt.name,
        pricing: editingCourt.pricing,
        is_active: editingCourt.is_active,
        description: editingCourt.description,
        base_price: editingCourt.base_price,
        peak_price: editingCourt.peak_price,
        weekend_price: editingCourt.weekend_price,
      });
      setCourtDialogOpen(false);
      loadCourts();
      toast.success("Đã cập nhật sân thành công");
    } catch (err) {
      setCourtError(formatApiError(err.response?.data?.detail));
    } finally {
      setCourtSubmitting(false);
    }
  };

  const handleToggleStatus = async (courtId) => {
    setTogglingCourt(courtId);
    try {
      await api.put(`/courts/${courtId}/toggle-status`);
      loadCourts();
      toast.success("Đã cập nhật trạng thái sân");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi cập nhật");
    } finally {
      setTogglingCourt(null);
    }
  };

  const updateSlotPrice = (dayIdx, slotIdx, newPrice) => {
    if (!editingCourt) return;
    const updated = { ...editingCourt };
    updated.pricing[dayIdx].time_slots[slotIdx].price = parseInt(newPrice) || 0;
    setEditingCourt(updated);
  };

  const roleMap = {
    ceo: { label: "CEO", className: "bg-lime-500/20 text-lime-400 border-lime-500/30" },
    staff: { label: "Nhân viên", className: "bg-sky-500/20 text-sky-400 border-sky-500/30" },
    customer: { label: "Khách hàng", className: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#F8FAFC] tracking-tight flex items-center gap-2">
          <Settings className="w-7 h-7 text-lime-500" />
          Cài đặt
        </h1>
        <p className="text-slate-400 text-sm mt-1">Quản lý sân, giá cả và người dùng</p>
      </div>

      <Tabs defaultValue="courts" className="w-full">
        <TabsList className="bg-[#1e293b] border border-[#334155] h-10">
          <TabsTrigger
            value="courts"
            className="data-[state=active]:bg-lime-500/10 data-[state=active]:text-lime-400"
            data-testid="tab-courts"
          >
            <MapPin className="w-4 h-4 mr-1.5" />
            Quản lý sân
          </TabsTrigger>
          <TabsTrigger
            value="users"
            className="data-[state=active]:bg-lime-500/10 data-[state=active]:text-lime-400"
            data-testid="tab-users"
          >
            <Users className="w-4 h-4 mr-1.5" />
            Người dùng
          </TabsTrigger>
          <TabsTrigger
            value="notifications"
            className="data-[state=active]:bg-lime-500/10 data-[state=active]:text-lime-400"
            data-testid="tab-notifications"
          >
            <Bell className="w-4 h-4 mr-1.5" />
            Thông báo
          </TabsTrigger>
          <TabsTrigger
            value="kiotviet"
            className="data-[state=active]:bg-lime-500/10 data-[state=active]:text-lime-400"
            data-testid="tab-kiotviet"
          >
            <Upload className="w-4 h-4 mr-1.5" />
            KiotViet
          </TabsTrigger>
          <TabsTrigger
            value="backup"
            className="data-[state=active]:bg-lime-500/10 data-[state=active]:text-lime-400"
            data-testid="tab-backup"
          >
            <HardDrive className="w-4 h-4 mr-1.5" />
            Auto Backup
          </TabsTrigger>
          <TabsTrigger
            value="zns-vars"
            className="data-[state=active]:bg-lime-500/10 data-[state=active]:text-lime-400"
            data-testid="tab-zns-vars"
          >
            <Code className="w-4 h-4 mr-1.5" />
            Biến ZNS
          </TabsTrigger>
          <TabsTrigger
            value="theme-color"
            className="data-[state=active]:bg-lime-500/10 data-[state=active]:text-lime-400"
            data-testid="tab-theme-color"
          >
            <Zap className="w-4 h-4 mr-1.5" />
            Giao diện
          </TabsTrigger>
          <TabsTrigger
            value="qrcode"
            className="data-[state=active]:bg-lime-500/10 data-[state=active]:text-lime-400"
            data-testid="tab-qrcode"
          >
            <QrCode className="w-4 h-4 mr-1.5" />
            Mã QR
          </TabsTrigger>
          <TabsTrigger
            value="shortlink"
            className="data-[state=active]:bg-sky-500/10 data-[state=active]:text-sky-400"
            data-testid="tab-shortlink"
          >
            <Link2 className="w-4 h-4 mr-1.5" />
            Rút gọn link
          </TabsTrigger>
        </TabsList>

        {/* Courts Tab */}
        <TabsContent value="courts" className="mt-4">
          {loadingCourts ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {Array(6).fill(0).map((_, i) => (
                <div key={i} className="h-64 bg-[#1e293b] border border-[#334155] rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="court-card-grid">
              {courts.map((court) => {
                const isSocial = court.court_type === "social";
                const occ = occupancyData[court.id] || {};
                const occPct = occ.occupancy_percent || 0;
                const isActive = court.status !== "maintenance" && court.is_active !== false;
                const weekday = court.pricing?.find((p) => p.day_type === "weekday");
                const weekend = court.pricing?.find((p) => p.day_type === "weekend");
                const minPrice = weekday?.time_slots?.length ? Math.min(...weekday.time_slots.map(s => s.price)) : (court.base_price || 0);
                const maxPrice = weekday?.time_slots?.length ? Math.max(...weekday.time_slots.map(s => s.price)) : (court.peak_price || 0);
                const wkndMax = weekend?.time_slots?.length ? Math.max(...weekend.time_slots.map(s => s.price)) : (court.weekend_price || 0);

                return (
                  <div
                    key={court.id}
                    data-testid={`court-card-${court.id}`}
                    className={`relative group rounded-xl border overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-black/20 ${
                      !isActive
                        ? "border-slate-600/40 opacity-70"
                        : isSocial
                        ? "border-teal-500/30"
                        : "border-sky-500/30"
                    }`}
                  >
                    {/* Gradient Header */}
                    <div className={`h-24 relative ${
                      !isActive
                        ? "bg-gradient-to-br from-slate-700 to-slate-800"
                        : isSocial
                        ? "bg-gradient-to-br from-teal-600/80 to-emerald-700/60"
                        : "bg-gradient-to-br from-sky-600/80 to-blue-700/60"
                    }`}>
                      {/* Status indicator */}
                      <div className="absolute top-3 left-3 flex items-center gap-2">
                        <Badge className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                          isSocial
                            ? "bg-teal-500/20 text-teal-200 border-teal-400/30"
                            : "bg-sky-500/20 text-sky-200 border-sky-400/30"
                        }`}>
                          {isSocial ? "Social" : "Thường"}
                        </Badge>
                        {court.court_group && (
                          <span className="text-[10px] text-white/60 font-mono">
                            Nhóm {court.court_group}
                          </span>
                        )}
                      </div>

                      {/* Active / Maintenance badge */}
                      <div className="absolute top-3 right-3">
                        {isActive ? (
                          <div className="flex items-center gap-1 text-[10px] text-emerald-300">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            Hoạt động
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-[10px] text-amber-300">
                            <Wrench className="w-3 h-3" />
                            Bảo trì
                          </div>
                        )}
                      </div>

                      {/* Court name */}
                      <div className="absolute bottom-3 left-3">
                        <h3 className="text-lg font-bold text-white drop-shadow-md">{court.name}</h3>
                        {court.description && (
                          <p className="text-[11px] text-white/70 mt-0.5 line-clamp-1">{court.description}</p>
                        )}
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="bg-[#1e293b] p-4 space-y-3">
                      {/* Occupancy Bar */}
                      <div data-testid={`court-occupancy-${court.id}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[11px] text-slate-400 flex items-center gap-1">
                            <Activity className="w-3 h-3" />
                            Công suất hôm nay
                          </span>
                          <span className={`text-xs font-bold ${
                            occPct >= 80 ? "text-red-400" : occPct >= 50 ? "text-amber-400" : "text-emerald-400"
                          }`}>
                            {occPct}%
                          </span>
                        </div>
                        <div className="h-2 bg-[#0f172a] rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-700 ${
                              occPct >= 80
                                ? "bg-gradient-to-r from-red-500 to-red-400"
                                : occPct >= 50
                                ? "bg-gradient-to-r from-amber-500 to-amber-400"
                                : "bg-gradient-to-r from-emerald-500 to-emerald-400"
                            }`}
                            style={{ width: `${occPct}%` }}
                          />
                        </div>
                        <div className="flex justify-between mt-1">
                          <span className="text-[10px] text-slate-500">
                            {occ.booking_count || 0} lượt đặt
                          </span>
                          <span className="text-[10px] text-slate-500">
                            {occ.booked_minutes || 0}/{occ.total_minutes || 1110} phút
                          </span>
                        </div>
                      </div>

                      {/* Price Badges */}
                      <div className="flex flex-wrap gap-1.5" data-testid={`court-prices-${court.id}`}>
                        <div className="flex items-center gap-1 bg-[#0f172a] rounded-lg px-2.5 py-1.5 border border-[#334155]">
                          <DollarSign className="w-3 h-3 text-emerald-400" />
                          <div>
                            <div className="text-[9px] text-slate-500 leading-none">Cơ bản</div>
                            <div className="text-xs font-bold text-emerald-400">{formatVND(minPrice)}đ</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 bg-[#0f172a] rounded-lg px-2.5 py-1.5 border border-[#334155]">
                          <TrendingUp className="w-3 h-3 text-orange-400" />
                          <div>
                            <div className="text-[9px] text-slate-500 leading-none">Cao điểm</div>
                            <div className="text-xs font-bold text-orange-400">{formatVND(maxPrice)}đ</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 bg-[#0f172a] rounded-lg px-2.5 py-1.5 border border-[#334155]">
                          <Calendar className="w-3 h-3 text-violet-400" />
                          <div>
                            <div className="text-[9px] text-slate-500 leading-none">Cuối tuần</div>
                            <div className="text-xs font-bold text-violet-400">{formatVND(wkndMax)}đ</div>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center gap-2 pt-1 border-t border-[#334155]/50">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openCourtEdit(court)}
                          className="flex-1 h-8 text-xs text-slate-300 hover:text-lime-400 hover:bg-lime-500/10 gap-1"
                          data-testid={`edit-court-${court.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          Chỉnh sửa
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleStatus(court.id)}
                          disabled={togglingCourt === court.id}
                          className={`h-8 text-xs gap-1 ${
                            isActive
                              ? "text-amber-400 hover:bg-amber-500/10"
                              : "text-emerald-400 hover:bg-emerald-500/10"
                          }`}
                          data-testid={`toggle-court-${court.id}`}
                        >
                          {togglingCourt === court.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : isActive ? (
                            <Wrench className="w-3.5 h-3.5" />
                          ) : (
                            <Power className="w-3.5 h-3.5" />
                          )}
                          {isActive ? "Bảo trì" : "Kích hoạt"}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setUserDialogOpen(true)} className="bg-lime-500 text-[#0f172a] font-semibold hover:bg-lime-400" data-testid="create-user-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Thêm nhân viên
            </Button>
          </div>
          <div className="bg-[#1e293b] border border-[#334155] rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-[#334155] hover:bg-transparent">
                  <TableHead className="text-slate-400">Họ tên</TableHead>
                  <TableHead className="text-slate-400">Email</TableHead>
                  <TableHead className="text-slate-400">Vai trò</TableHead>
                  <TableHead className="text-slate-400">Số điện thoại</TableHead>
                  <TableHead className="text-slate-400 text-right">Thao tác</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingUsers ? (
                  Array(3).fill(0).map((_, i) => (
                    <TableRow key={i} className="border-[#334155]">
                      <TableCell colSpan={5}><div className="h-6 bg-[#334155] rounded animate-pulse" /></TableCell>
                    </TableRow>
                  ))
                ) : users.filter(u => u.role === "ceo" || u.role === "staff").map(u => {
                  const r = roleMap[u.role] || roleMap.staff;
                  return (
                    <TableRow key={u.id} className="border-[#334155] hover:bg-[#334155]/30" data-testid={`user-row-${u.id}`}>
                      <TableCell className="text-slate-200 font-medium">{u.name}</TableCell>
                      <TableCell className="text-slate-400 text-sm">{u.email}</TableCell>
                      <TableCell><Badge className={`${r.className} text-xs rounded-full border`}>{r.label}</Badge></TableCell>
                      <TableCell className="text-slate-400 text-sm">{u.phone || "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openUserLogs(u)} className="text-slate-400 hover:text-sky-400 hover:bg-sky-500/10" title="Xem log" data-testid={`log-user-${u.id}`}>
                            <History className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditUser(u)} className="text-slate-400 hover:text-amber-400 hover:bg-amber-500/10" title="Sửa" data-testid={`edit-user-${u.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {isCeo && u.role !== "ceo" && (
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u.id)} className="text-slate-400 hover:text-red-400 hover:bg-red-500/10" title="Xóa" data-testid={`delete-user-${u.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Edit User Dialog */}
          <Dialog open={editUserOpen} onOpenChange={setEditUserOpen}>
            <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-md" data-testid="edit-user-modal">
              <DialogHeader>
                <DialogTitle className="text-[#F8FAFC]">Chỉnh sửa — {editUser?.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-slate-400 text-xs">Họ tên</Label>
                  <Input value={editUserData.name} onChange={e => setEditUserData(d => ({ ...d, name: e.target.value }))} className="bg-[#0f172a] border-[#334155] text-slate-50" />
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-400 text-xs">Email</Label>
                  <Input value={editUserData.email} onChange={e => setEditUserData(d => ({ ...d, email: e.target.value }))} className="bg-[#0f172a] border-[#334155] text-slate-50" />
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-400 text-xs">Số điện thoại</Label>
                  <Input value={editUserData.phone} onChange={e => setEditUserData(d => ({ ...d, phone: e.target.value }))} className="bg-[#0f172a] border-[#334155] text-slate-50" />
                </div>
                {isCeo && (
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-xs">Vai trò</Label>
                    <Select value={editUserData.role} onValueChange={v => setEditUserData(d => ({ ...d, role: v }))}>
                      <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-50"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#1e293b] border-[#334155]">
                        <SelectItem value="ceo" className="text-slate-300">CEO</SelectItem>
                        <SelectItem value="staff" className="text-slate-300">Staff</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setEditUserOpen(false)} className="text-slate-400">Hủy</Button>
                <Button onClick={handleEditUser} disabled={editUserSaving} className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold">
                  {editUserSaving ? "Đang lưu..." : "Lưu"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Activity Log Dialog */}
          <Dialog open={logOpen} onOpenChange={setLogOpen}>
            <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-xl max-h-[80vh] overflow-y-auto" data-testid="log-dialog">
              <DialogHeader>
                <DialogTitle className="text-[#F8FAFC] flex items-center gap-2">
                  <History className="w-4 h-4 text-sky-400" /> Log thao tác — {logUser?.name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {logsLoading ? (
                  <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" /></div>
                ) : activityLogs.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-8">Chưa có log thao tác</p>
                ) : activityLogs.map(lg => {
                  const actionColors = { create: "text-lime-400", update: "text-amber-400", delete: "text-red-400", import: "text-sky-400", send: "text-emerald-400" };
                  const actionLabels = { create: "Tạo", update: "Sửa", delete: "Xóa", import: "Import", send: "Gửi" };
                  return (
                    <div key={lg.id} className="flex items-start gap-3 bg-[#0f172a] border border-[#334155] rounded-lg p-3">
                      <div className={`text-xs font-bold px-2 py-0.5 rounded ${actionColors[lg.action] || "text-slate-400"}`}>
                        {actionLabels[lg.action] || lg.action}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-300">{lg.details}</p>
                        <p className="text-[10px] text-slate-600 mt-0.5">
                          {lg.target} · {new Date(lg.created_at).toLocaleString("vi-VN")}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="mt-4">
          <div className="space-y-4">
            {/* Zalo ZNS Connection Status */}
            <div className={`border rounded-lg p-4 flex items-center justify-between ${
              notiSettings.zalo_connected ? "bg-lime-500/5 border-lime-500/20" :
              notiSettings.zalo_configured ? "bg-amber-500/5 border-amber-500/20" :
              "bg-slate-500/5 border-slate-500/20"
            }`} data-testid="zalo-status-card">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${
                  notiSettings.zalo_connected ? "bg-lime-400" :
                  notiSettings.zalo_configured ? "bg-amber-400 animate-pulse" : "bg-slate-500"
                }`} />
                <div>
                  <p className="text-sm font-medium text-[#F8FAFC]">
                    {notiSettings.zalo_connected ? "Zalo OA đã kết nối" :
                     notiSettings.zalo_configured ? "Zalo OA đã cấu hình - chưa kết nối" :
                     "Zalo OA chưa cấu hình"}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {notiSettings.zalo_cred_oa_id ? `OA ID: ${notiSettings.zalo_cred_oa_id}` : "Chưa có thông tin OA"}
                    {notiSettings.zalo_cred_app_id ? ` · App ID: ${notiSettings.zalo_cred_app_id}` : ""}
                  </p>
                </div>
              </div>
              <Button
                onClick={handleTestZaloConnection}
                disabled={zaloTesting}
                variant="outline"
                className="border-[#334155] text-slate-300 hover:bg-[#334155] gap-1.5 text-xs h-8"
                data-testid="test-zalo-btn"
              >
                {zaloTesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wifi className="w-3.5 h-3.5" />}
                {zaloTesting ? "Đang kiểm tra..." : "Kiểm tra kết nối"}
              </Button>
            </div>

            {zaloTestResult && (
              <div className={`text-xs p-2 rounded ${zaloTestResult.success ? "bg-lime-500/10 text-lime-400" : "bg-red-500/10 text-red-400"}`} data-testid="zalo-test-result">
                {zaloTestResult.message}
                {zaloTestResult.expires_at && <span className="ml-2 text-slate-500">· Token hết hạn: {new Date(zaloTestResult.expires_at).toLocaleString("vi-VN")}</span>}
              </div>
            )}

            {/* Zalo Credentials / Token Config */}
            <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-5 space-y-4" data-testid="zalo-credentials-section">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-[#F8FAFC] flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-amber-400" />
                  Zalo API Credentials
                </h3>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={handleZaloRefreshNow}
                    disabled={zaloRefreshing || !notiSettings.zalo_configured}
                    variant="outline"
                    className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 gap-1.5 text-xs h-8"
                    data-testid="zalo-refresh-now-btn"
                  >
                    {zaloRefreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    {zaloRefreshing ? "Đang refresh..." : "Refresh token ngay"}
                  </Button>
                  <Button
                    onClick={handleZaloDiagnose}
                    variant="outline"
                    className="border-purple-500/40 text-purple-400 hover:bg-purple-500/10 gap-1.5 text-xs h-8"
                    data-testid="zalo-diagnose-btn"
                  >
                    <Code className="w-3.5 h-3.5" />
                    Chẩn đoán Zalo
                  </Button>
                  <Button
                    onClick={() => { setZaloTestZnsOpen(true); setZaloTestZnsResult(null); }}
                    disabled={!notiSettings.zalo_connected}
                    variant="outline"
                    className="border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10 gap-1.5 text-xs h-8"
                    data-testid="zalo-test-zns-btn"
                  >
                    <Send className="w-3.5 h-3.5" />
                    Test gửi ZNS
                  </Button>
                </div>
              </div>

              {/* Circuit breaker alert (chỉ hiện khi OPEN) */}
              {(() => {
                const cb = notiSettings.zalo_circuit_state || {};
                const openUntil = cb.open_until;
                if (!openUntil) return null;
                const until = new Date(openUntil);
                if (until.getTime() <= Date.now()) return null;
                return (
                  <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-3 flex items-start gap-2" data-testid="zalo-circuit-alert">
                    <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-red-300">Circuit Breaker đang OPEN</p>
                      <p className="text-[11px] text-red-200/80 mt-0.5">
                        {cb.failure_count || 0} lần refresh fail liên tiếp. Auto-refresh tạm dừng đến{" "}
                        <span className="font-mono">{until.toLocaleString("vi-VN", { hour12: false })}</span>.
                        Vui lòng <strong>Chẩn đoán Zalo</strong> để tìm root cause, sau khi fix xong → Reset.
                      </p>
                      {cb.last_error && (
                        <p className="text-[10px] text-red-200/60 mt-1 break-all">Lỗi gần nhất: {cb.last_error}</p>
                      )}
                      <Button
                        onClick={handleZaloResetCircuit}
                        disabled={zaloResetting}
                        size="sm"
                        variant="outline"
                        className="mt-2 border-red-500/40 text-red-300 hover:bg-red-500/20 gap-1.5 text-[11px] h-7"
                        data-testid="zalo-reset-circuit-btn"
                      >
                        {zaloResetting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        Reset circuit breaker
                      </Button>
                    </div>
                  </div>
                );
              })()}

              {/* Monitoring panel (auto-refresh 20s) */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="zalo-monitoring-panel">
                <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-3">
                  <p className="text-[10px] text-slate-500 uppercase font-medium mb-1">Token đang dùng (DB)</p>
                  <p className="font-mono text-xs text-slate-200 break-all" data-testid="zalo-db-token-preview">
                    {notiSettings.zalo_db_access_token
                      ? `…${notiSettings.zalo_db_access_token.slice(-16)}`
                      : (notiSettings.zalo_access_token ? `…${(notiSettings.zalo_access_token || "").slice(-16)}` : "—")}
                  </p>
                  <p className="text-[10px] text-slate-600 mt-1">Cập nhật từ DB mỗi 20s</p>
                </div>
                <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-3">
                  <p className="text-[10px] text-slate-500 uppercase font-medium mb-1">Hết hạn</p>
                  <p className="text-xs text-slate-200" data-testid="zalo-expires-display">
                    {notiSettings.zalo_expires_at
                      ? new Date(notiSettings.zalo_expires_at).toLocaleString("vi-VN", { hour12: false })
                      : "—"}
                  </p>
                  {notiSettings.zalo_expires_at && (() => {
                    const diff = new Date(notiSettings.zalo_expires_at).getTime() - Date.now();
                    const hours = Math.floor(diff / 3600000);
                    if (diff <= 0) return <p className="text-[10px] text-red-400 mt-1">Đã hết hạn — sẽ tự refresh khi gửi ZNS</p>;
                    if (hours < 2) return <p className="text-[10px] text-amber-400 mt-1">Sắp hết — còn {hours}h</p>;
                    return <p className="text-[10px] text-emerald-400 mt-1">Còn {hours}h</p>;
                  })()}
                </div>
                <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-3">
                  <p className="text-[10px] text-slate-500 uppercase font-medium mb-1">Refresh lần cuối</p>
                  <p className="text-xs text-slate-200" data-testid="zalo-last-refresh-display">
                    {notiSettings.zalo_last_refresh_at
                      ? new Date(notiSettings.zalo_last_refresh_at).toLocaleString("vi-VN", { hour12: false })
                      : "—"}
                  </p>
                  {notiSettings.zalo_last_refresh_error ? (
                    <p className="text-[10px] text-red-400 mt-1 break-all" data-testid="zalo-last-refresh-error">
                      Lỗi: {notiSettings.zalo_last_refresh_error}
                    </p>
                  ) : (
                    <p className="text-[10px] text-emerald-400 mt-1">OK — không có lỗi</p>
                  )}
                </div>
              </div>

              <p className="text-xs text-slate-500">
                Lấy Access Token & Refresh Token từ <a href="https://developers.zalo.me/tools/explorer" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline">Zalo API Explorer</a> hoặc OAuth flow.
                Hệ thống TỰ ĐỘNG refresh access_token định kỳ; bạn chỉ cần paste 1 lần khi setup ban đầu.
              </p>

              {/* App credentials editable — sửa khi app_secret bị rotate trong Zalo Console */}
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 space-y-3">
                <p className="text-[11px] text-amber-300 font-medium">
                  ⚙️ App Credentials (chỉ paste khi cần update — vd: Zalo rotate app_secret)
                </p>
                {/* App ID validate: phải 17-18 chữ số nguyên (Zalo standard).
                    Bug case 113.10: app_id thừa 1 ký tự `2` → -14002 Invalid appId. */}
                {(() => {
                  const aid = (notiSettings.zalo_cred_app_id || "").trim();
                  if (!aid) return null;
                  const isValid = /^\d{17,18}$/.test(aid);
                  if (isValid) return null;
                  return (
                    <div className="bg-red-500/10 border border-red-500/40 rounded p-3 text-xs text-red-300" data-testid="app-id-pattern-warning">
                      <p className="font-bold text-red-200 mb-1">⚠️ App ID không đúng chuẩn Zalo</p>
                      <p>
                        App ID hiện tại: <code className="font-mono bg-red-500/20 px-1 rounded">{aid}</code> ({aid.length} ký tự)
                      </p>
                      <p className="mt-1">
                        Chuẩn Zalo: <strong>17-18 chữ số nguyên</strong> (chỉ chứa 0-9). Hệ thống sẽ trả lỗi <code>-14002 Invalid appId</code> khi gửi ZNS.
                      </p>
                      <p className="mt-1 text-red-200">
                        ✅ Vui lòng copy lại từ <a href="https://developers.zalo.me/apps" target="_blank" rel="noopener noreferrer" className="underline">developers.zalo.me/apps</a> → app KARINOX → tab "App information" → "ID ứng dụng".
                      </p>
                    </div>
                  );
                })()}
                {/* App secret length warning */}
                {(notiSettings.zalo_cred_app_secret_length || 0) > 100 && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded p-2 text-[11px] text-red-300" data-testid="app-secret-len-warning">
                    ⚠️ App Secret hiện đang lưu dài <strong>{notiSettings.zalo_cred_app_secret_length}</strong> ký tự —
                    quá dài so với chuẩn Zalo (~32 ký tự). Có thể đã bị dán nhầm refresh_token vào field secret.
                    Vui lòng copy lại <strong>App Secret</strong> chính xác từ Zalo Console và paste vào ô bên dưới.
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-xs">App ID hiện tại</Label>
                    <Input
                      value={notiSettings.zalo_cred_app_id || ""}
                      readOnly
                      className="bg-[#0f172a]/60 border-[#334155] text-slate-400 font-mono text-xs cursor-default"
                      data-testid="zalo-cred-app-id-readonly"
                    />
                    <Input
                      value={notiSettings.zalo_app_id_input || ""}
                      onChange={(e) => setNotiSettings(s => ({ ...s, zalo_app_id_input: e.target.value }))}
                      placeholder="Paste App ID mới (để trống nếu giữ nguyên)..."
                      className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-600 focus:border-amber-500 font-mono text-xs"
                      data-testid="zalo-app-id-input"
                    />
                    {/* Inline live validation */}
                    {(notiSettings.zalo_app_id_input || "").trim() && !/^\d{17,18}$/.test((notiSettings.zalo_app_id_input || "").trim()) && (
                      <p className="text-[10px] text-red-400" data-testid="zalo-app-id-input-warning">
                        ❌ App ID phải là 17-18 chữ số nguyên (đang nhập {(notiSettings.zalo_app_id_input || "").trim().length} ký tự)
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-xs">App Secret hiện tại ({notiSettings.zalo_cred_app_secret_length || 0} ký tự)</Label>
                    <Input
                      value={notiSettings.zalo_cred_app_secret_preview || ""}
                      readOnly
                      className="bg-[#0f172a]/60 border-[#334155] text-slate-400 font-mono text-xs cursor-default"
                      data-testid="zalo-cred-app-secret-readonly"
                    />
                    <Input
                      value={notiSettings.zalo_app_secret_input || ""}
                      onChange={(e) => setNotiSettings(s => ({ ...s, zalo_app_secret_input: e.target.value }))}
                      placeholder="Paste App Secret mới (để trống nếu giữ nguyên)..."
                      className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-600 focus:border-amber-500 font-mono text-xs"
                      data-testid="zalo-app-secret-input"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 italic">
                  💡 Lấy từ <a href="https://developers.zalo.me/apps" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline">developers.zalo.me/apps</a> → mở app → tab "App information".
                  Sau khi paste + Lưu: circuit breaker sẽ tự reset, hệ thống thử refresh ngay với credentials mới.
                </p>
              </div>

              {/* ZNS Proxy API — cho hệ thống bên ngoài (vd PickLeague) dùng chung Zalo creds */}
              <div className="bg-violet-500/5 border border-violet-500/20 rounded-lg p-3 space-y-3" data-testid="zns-proxy-section">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-xs text-violet-300 font-medium flex items-center gap-1.5">
                    <KeyRound className="w-3.5 h-3.5" />
                    🔐 ZNS Proxy API (cho hệ thống bên ngoài dùng chung Zalo creds)
                  </p>
                  {znsProxyConfig && (
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400">
                        {znsProxyConfig.enabled ? "Đang bật" : "Đã tắt"}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleToggleProxyEnabled(!znsProxyConfig.enabled)}
                        className={`relative w-10 h-5 rounded-full transition ${
                          znsProxyConfig.enabled ? "bg-violet-500" : "bg-slate-600"
                        }`}
                        data-testid="zns-proxy-toggle"
                      >
                        <span
                          className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition ${
                            znsProxyConfig.enabled ? "left-5" : "left-0.5"
                          }`}
                        />
                      </button>
                    </div>
                  )}
                </div>

                {!znsProxyConfig ? (
                  <p className="text-[11px] text-slate-500">Đang tải cấu hình…</p>
                ) : (
                  <>
                    {/* Endpoint URL */}
                    <div className="space-y-1">
                      <Label className="text-slate-400 text-[10px] flex items-center gap-1">
                        <LinkIcon className="w-3 h-3" />
                        Endpoint URL (gửi cho hệ thống bên ngoài)
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          value={znsProxyConfig.endpoint_url || `${znsProxyConfig.public_base_url || ""}${znsProxyConfig.endpoint_path || ""}`}
                          readOnly
                          className="bg-[#0f172a] border-[#334155] text-slate-200 font-mono text-xs flex-1"
                          data-testid="zns-proxy-url-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopy(
                            znsProxyConfig.endpoint_url || `${znsProxyConfig.public_base_url || ""}${znsProxyConfig.endpoint_path || ""}`,
                            "Đã sao chép URL"
                          )}
                          className="border-violet-500/40 text-violet-300 hover:bg-violet-500/10 gap-1 text-[11px] h-9"
                          data-testid="zns-proxy-copy-url-btn"
                        >
                          <Copy className="w-3 h-3" />
                          Copy
                        </Button>
                      </div>
                      {/* Editable public_base_url để CEO đổi domain hiển thị nếu cần */}
                      <details className="text-[10px] text-slate-500 mt-1">
                        <summary className="cursor-pointer hover:text-slate-400">Đổi domain hiển thị (advanced)</summary>
                        <div className="flex gap-2 mt-2">
                          <Input
                            value={notiSettings.zns_proxy_base_url_input ?? (znsProxyConfig.public_base_url || "")}
                            onChange={(e) => setNotiSettings(s => ({ ...s, zns_proxy_base_url_input: e.target.value }))}
                            placeholder="https://datlich.karinox.vn"
                            className="bg-[#0f172a] border-[#334155] text-slate-200 font-mono text-xs flex-1"
                            data-testid="zns-proxy-base-url-input"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              const val = (notiSettings.zns_proxy_base_url_input ?? "").trim();
                              if (!val) { toast.error("Vui lòng nhập URL"); return; }
                              if (!/^https?:\/\/[^\s]+$/.test(val)) { toast.error("URL phải bắt đầu bằng http:// hoặc https://"); return; }
                              try {
                                await api.put("/zns/proxy/config", { public_base_url: val });
                                toast.success("Đã cập nhật domain endpoint");
                                setNotiSettings(s => ({ ...s, zns_proxy_base_url_input: undefined }));
                                await loadZnsProxyConfig();
                              } catch (err) {
                                toast.error(formatApiError(err.response?.data?.detail) || "Lỗi cập nhật domain");
                              }
                            }}
                            className="border-violet-500/40 text-violet-300 hover:bg-violet-500/10 gap-1 text-[11px] h-8"
                            data-testid="zns-proxy-save-base-url-btn"
                          >
                            <Save className="w-3 h-3" />
                            Lưu
                          </Button>
                        </div>
                        <p className="text-[10px] text-slate-600 mt-1 italic">
                          Default: <code>https://datlich.karinox.vn</code>. Đổi nếu anh dùng domain production khác.
                        </p>
                      </details>
                    </div>

                    {/* API Key */}
                    <div className="space-y-1">
                      <Label className="text-slate-400 text-[10px] flex items-center gap-1">
                        <KeyRound className="w-3 h-3" />
                        API Key (Bearer token)
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          type={znsProxyKeyVisible ? "text" : "password"}
                          value={znsProxyConfig.api_key || ""}
                          readOnly
                          className="bg-[#0f172a] border-[#334155] text-slate-200 font-mono text-xs flex-1"
                          data-testid="zns-proxy-key-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setZnsProxyKeyVisible(v => !v)}
                          className="border-slate-500/40 text-slate-300 hover:bg-slate-500/10 gap-1 text-[11px] h-9"
                          data-testid="zns-proxy-toggle-visibility-btn"
                        >
                          {znsProxyKeyVisible ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopy(znsProxyConfig.api_key || "", "Đã sao chép API Key")}
                          className="border-violet-500/40 text-violet-300 hover:bg-violet-500/10 gap-1 text-[11px] h-9"
                          data-testid="zns-proxy-copy-key-btn"
                        >
                          <Copy className="w-3 h-3" />
                          Copy
                        </Button>
                      </div>
                    </div>

                    {/* Usage stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-1">
                      <div className="bg-[#0f172a]/60 border border-[#334155] rounded p-2">
                        <p className="text-[9px] text-slate-500 uppercase">Tổng request</p>
                        <p className="text-sm text-slate-100 font-semibold">{znsProxyConfig.usage_total || 0}</p>
                      </div>
                      <div className="bg-emerald-500/5 border border-emerald-500/20 rounded p-2">
                        <p className="text-[9px] text-emerald-400 uppercase">Thành công</p>
                        <p className="text-sm text-emerald-300 font-semibold">{znsProxyConfig.usage_success || 0}</p>
                      </div>
                      <div className="bg-red-500/5 border border-red-500/20 rounded p-2">
                        <p className="text-[9px] text-red-400 uppercase">Thất bại</p>
                        <p className="text-sm text-red-300 font-semibold">{znsProxyConfig.usage_fail || 0}</p>
                      </div>
                      <div className="bg-[#0f172a]/60 border border-[#334155] rounded p-2">
                        <p className="text-[9px] text-slate-500 uppercase">Lần dùng gần nhất</p>
                        <p className="text-[11px] text-slate-300 font-mono">
                          {znsProxyConfig.last_used_at
                            ? new Date(znsProxyConfig.last_used_at).toLocaleString("vi-VN")
                            : "Chưa có"}
                        </p>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleRegenerateProxyKey}
                        disabled={znsProxyRegenerating}
                        className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 gap-1.5 text-[11px] h-8"
                        data-testid="zns-proxy-regenerate-btn"
                      >
                        {znsProxyRegenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        Tạo lại API Key
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setZnsProxyExampleOpen(v => !v)}
                        className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 gap-1.5 text-[11px] h-8"
                        data-testid="zns-proxy-show-example-btn"
                      >
                        <Code className="w-3 h-3" />
                        {znsProxyExampleOpen ? "Ẩn ví dụ curl" : "Hiện ví dụ curl"}
                      </Button>
                    </div>

                    {/* Code example */}
                    {znsProxyExampleOpen && (
                      <div className="bg-[#0f172a]/80 border border-[#334155] rounded p-3 space-y-2" data-testid="zns-proxy-example-box">
                        <p className="text-[10px] text-slate-400">Ví dụ gọi từ hệ thống bên ngoài (curl):</p>
                        <pre className="text-[10px] text-emerald-300 font-mono overflow-x-auto whitespace-pre">{`curl -X POST "${znsProxyConfig.endpoint_url || ((znsProxyConfig.public_base_url || "") + (znsProxyConfig.endpoint_path || ""))}" \\
  -H "Authorization: Bearer ${znsProxyKeyVisible ? (znsProxyConfig.api_key || "<API_KEY>") : "<API_KEY>"}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "phone": "0901234567",
    "otp_code": "123456",
    "purpose": "register_pickleague"
  }'

# Response 200 OK:
# {"ok": true, "message_id": "abc123..."}

# Response 4xx/5xx:
# {"ok": false, "error": "..."}`}</pre>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopy(
                            `curl -X POST "${znsProxyConfig.endpoint_url || ((znsProxyConfig.public_base_url || "") + (znsProxyConfig.endpoint_path || ""))}" \\\n` +
                            `  -H "Authorization: Bearer ${znsProxyConfig.api_key || "<API_KEY>"}" \\\n` +
                            `  -H "Content-Type: application/json" \\\n` +
                            `  -d '{"phone":"0901234567","otp_code":"123456","purpose":"register_pickleague"}'`,
                            "Đã sao chép câu lệnh curl"
                          )}
                          className="border-slate-500/40 text-slate-300 hover:bg-slate-500/10 gap-1 text-[11px] h-7"
                          data-testid="zns-proxy-copy-curl-btn"
                        >
                          <Copy className="w-3 h-3" />
                          Sao chép câu lệnh
                        </Button>
                      </div>
                    )}

                    <p className="text-[10px] text-slate-500 italic">
                      💡 Endpoint dùng template OTP của Zalo đang cấu hình (zns_template_otp).
                      Mỗi request được log vào hệ thống (collection <code className="text-slate-400">notification_logs</code> với source <code className="text-slate-400">zns_proxy</code>).
                      Hệ thống bên ngoài KHÔNG cần biết App Secret/Refresh Token Zalo — chỉ cần API Key này.
                    </p>
                  </>
                )}
              </div>

              {/* ZNS Proxy LOGS — xem requests từ hệ thống bên ngoài */}
              <div className="bg-indigo-500/5 border border-indigo-500/20 rounded-lg p-3 space-y-3" data-testid="zns-proxy-logs-section">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-xs text-indigo-300 font-medium flex items-center gap-1.5">
                    <History className="w-3.5 h-3.5" />
                    📜 Lịch sử request ZNS Proxy (từ hệ thống bên ngoài)
                  </p>
                  <div className="flex items-center gap-2">
                    {znsProxyLogs && (
                      <div className="flex items-center gap-2 text-[10px]">
                        <span className="text-slate-400">
                          Tổng: <strong className="text-slate-200">{znsProxyLogs.total || 0}</strong>
                        </span>
                        <span className="text-emerald-400">
                          ✓ {znsProxyLogs.success_count || 0}
                        </span>
                        <span className="text-red-400">
                          ✗ {znsProxyLogs.fail_count || 0}
                        </span>
                      </div>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => loadZnsProxyLogs(znsProxyLogsFilter)}
                      disabled={znsProxyLogsLoading}
                      className="border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10 gap-1.5 text-[11px] h-7"
                      data-testid="zns-proxy-logs-refresh-btn"
                    >
                      {znsProxyLogsLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      Tải lại
                    </Button>
                  </div>
                </div>

                {/* Filter buttons */}
                <div className="flex flex-wrap gap-2" data-testid="zns-proxy-logs-filters">
                  {[
                    { key: "all", label: "Tất cả", color: "slate" },
                    { key: "success", label: "Thành công", color: "emerald" },
                    { key: "fail", label: "Thất bại", color: "red" },
                  ].map(opt => {
                    const active = znsProxyLogsFilter === opt.key;
                    const colorMap = {
                      slate: active ? "bg-slate-500 text-white border-slate-500" : "bg-transparent text-slate-300 border-slate-500/40 hover:bg-slate-500/10",
                      emerald: active ? "bg-emerald-500 text-white border-emerald-500" : "bg-transparent text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/10",
                      red: active ? "bg-red-500 text-white border-red-500" : "bg-transparent text-red-300 border-red-500/40 hover:bg-red-500/10",
                    };
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={async () => {
                          setZnsProxyLogsFilter(opt.key);
                          await loadZnsProxyLogs(opt.key);
                        }}
                        className={`px-3 py-1 rounded border text-[11px] transition ${colorMap[opt.color]}`}
                        data-testid={`zns-proxy-logs-filter-${opt.key}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>

                {/* Logs table */}
                {!znsProxyLogs ? (
                  <p className="text-[11px] text-slate-500">Đang tải lịch sử…</p>
                ) : (znsProxyLogs.entries || []).length === 0 ? (
                  <div className="bg-[#0f172a]/60 border border-[#334155] rounded p-4 text-center" data-testid="zns-proxy-logs-empty">
                    <p className="text-xs text-slate-400">
                      {znsProxyLogsFilter === "all"
                        ? "Chưa có request nào từ hệ thống bên ngoài."
                        : `Không có request ${znsProxyLogsFilter === "success" ? "thành công" : "thất bại"} nào.`}
                    </p>
                    <p className="text-[10px] text-slate-600 italic mt-1">
                      Khi PickLeague (hoặc hệ thống khác) gọi endpoint trên, log sẽ hiển thị ở đây.
                    </p>
                  </div>
                ) : (
                  <div className="bg-[#0f172a]/60 border border-[#334155] rounded overflow-hidden" data-testid="zns-proxy-logs-table">
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px]">
                        <thead className="bg-[#1e293b] text-slate-400 uppercase text-[10px]">
                          <tr>
                            <th className="px-2 py-2 text-left">Thời gian</th>
                            <th className="px-2 py-2 text-left">SDT</th>
                            <th className="px-2 py-2 text-left">Mục đích</th>
                            <th className="px-2 py-2 text-center">Trạng thái</th>
                            <th className="px-2 py-2 text-left">Msg ID / Lỗi</th>
                            <th className="px-2 py-2 text-left">IP</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700">
                          {(znsProxyLogs.entries || []).map((log, idx) => {
                            const isExpanded = !!znsProxyLogsExpanded[idx];
                            return (
                              <React.Fragment key={idx}>
                                <tr
                                  className={`hover:bg-slate-700/30 cursor-pointer ${log.success ? "" : "bg-red-500/5"}`}
                                  onClick={() => setZnsProxyLogsExpanded(s => ({ ...s, [idx]: !s[idx] }))}
                                  data-testid={`zns-proxy-log-row-${idx}`}
                                >
                                  <td className="px-2 py-1.5 text-slate-300 font-mono whitespace-nowrap">
                                    {log.created_at ? new Date(log.created_at).toLocaleString("vi-VN") : "-"}
                                  </td>
                                  <td className="px-2 py-1.5 text-slate-200 font-mono">{log.phone || "-"}</td>
                                  <td className="px-2 py-1.5 text-slate-400">
                                    {log.purpose ? (
                                      <code className="text-[10px] bg-slate-700/50 px-1 rounded">{log.purpose}</code>
                                    ) : <span className="text-slate-600">-</span>}
                                  </td>
                                  <td className="px-2 py-1.5 text-center">
                                    {log.success ? (
                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 text-[10px] font-medium">
                                        ✓ OK
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 text-[10px] font-medium">
                                        ✗ FAIL
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-2 py-1.5 text-slate-300 font-mono max-w-[280px] truncate">
                                    {log.success ? (
                                      <span className="text-emerald-300">{log.msg_id || "-"}</span>
                                    ) : (
                                      <span className="text-red-300" title={log.error}>{log.error || "(no error)"}</span>
                                    )}
                                  </td>
                                  <td className="px-2 py-1.5 text-slate-500 font-mono">{log.client_ip || "-"}</td>
                                </tr>
                                {isExpanded && (
                                  <tr className="bg-slate-900/50">
                                    <td colSpan={6} className="px-3 py-2">
                                      <pre className="text-[10px] text-slate-300 font-mono overflow-x-auto whitespace-pre-wrap">
{JSON.stringify({
  phone: log.phone,
  purpose: log.purpose,
  template_id: log.template_id,
  success: log.success,
  msg_id: log.msg_id,
  error: log.error,
  client_ip: log.client_ip,
  created_at: log.created_at,
  raw: log.raw,
}, null, 2)}
                                      </pre>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {znsProxyLogs.total > (znsProxyLogs.entries || []).length && (
                      <div className="px-3 py-2 bg-slate-900/40 text-[10px] text-slate-500 text-center border-t border-slate-700">
                        Hiển thị {(znsProxyLogs.entries || []).length} / {znsProxyLogs.total} requests gần nhất.
                      </div>
                    )}
                  </div>
                )}

                <p className="text-[10px] text-slate-500 italic">
                  💡 Click vào từng dòng để xem chi tiết raw response từ Zalo. Log lưu trong <code className="text-slate-400">notification_logs</code> với <code className="text-slate-400">channel="zns_proxy"</code>.
                </p>
              </div>

              {/* Health Probe / Synthetic Monitoring */}
              <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-3 space-y-3" data-testid="zalo-health-probe-section">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-[11px] text-cyan-300 font-medium">
                    🩺 ZNS Health Probe (cron mỗi {notiSettings.zalo_probe_interval_hours || 8}h · tối đa {notiSettings.zalo_probe_max_per_day || 3} lần/ngày)
                  </p>
                  <Button
                    onClick={handleZaloProbeNow}
                    disabled={zaloProbing}
                    size="sm"
                    variant="outline"
                    className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 gap-1.5 text-[11px] h-7"
                    data-testid="zalo-probe-now-btn"
                  >
                    {zaloProbing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    Probe ngay
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-[10px]">SDT nhận probe (mặc định 0938811678)</Label>
                    <Input
                      value={notiSettings.zalo_probe_phone || ""}
                      onChange={(e) => setNotiSettings(s => ({ ...s, zalo_probe_phone: e.target.value }))}
                      placeholder="0938811678"
                      className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-600 focus:border-cyan-500 font-mono text-xs"
                      data-testid="zalo-probe-phone-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-[10px]">Bật/tắt probe tự động</Label>
                    <div className="flex items-center gap-2 h-9">
                      <button
                        type="button"
                        onClick={() => setNotiSettings(s => ({ ...s, zalo_probe_enabled: !s.zalo_probe_enabled }))}
                        className={`relative w-12 h-6 rounded-full transition ${
                          notiSettings.zalo_probe_enabled ? "bg-cyan-500" : "bg-slate-600"
                        }`}
                        data-testid="zalo-probe-enabled-toggle"
                      >
                        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition ${
                          notiSettings.zalo_probe_enabled ? "left-6" : "left-0.5"
                        }`} />
                      </button>
                      <span className="text-xs text-slate-400">
                        {notiSettings.zalo_probe_enabled ? "Đang bật" : "Đang tắt"}
                      </span>
                    </div>
                  </div>
                  {/* v114.8: Tùy chỉnh chu kỳ + tần suất */}
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-[10px]">Số giờ giữa các probe (1-24)</Label>
                    <Input
                      type="number" min={1} max={24}
                      value={notiSettings.zalo_probe_interval_hours ?? 8}
                      onChange={(e) => setNotiSettings(s => ({
                        ...s, zalo_probe_interval_hours: Math.max(1, Math.min(24, Number(e.target.value) || 8)),
                      }))}
                      placeholder="8"
                      className="bg-[#0f172a] border-[#334155] text-slate-50 focus:border-cyan-500 font-mono text-xs"
                      data-testid="zalo-probe-interval-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-[10px]">Số lần tối đa / ngày (1-24)</Label>
                    <Input
                      type="number" min={1} max={24}
                      value={notiSettings.zalo_probe_max_per_day ?? 3}
                      onChange={(e) => setNotiSettings(s => ({
                        ...s, zalo_probe_max_per_day: Math.max(1, Math.min(24, Number(e.target.value) || 3)),
                      }))}
                      placeholder="3"
                      className="bg-[#0f172a] border-[#334155] text-slate-50 focus:border-cyan-500 font-mono text-xs"
                      data-testid="zalo-probe-max-per-day-input"
                    />
                  </div>
                </div>

                {/* Last probe state */}
                {(() => {
                  const state = notiSettings.zalo_probe_state || zaloProbeStatus?.state || {};
                  if (!state.last_at) {
                    return (
                      <p className="text-[10px] text-slate-500 italic">
                        Chưa có probe nào — bấm "Probe ngay" để test thử.
                      </p>
                    );
                  }
                  return (
                    <div className={`text-[11px] p-2 rounded border ${
                      state.last_success
                        ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                        : "bg-red-500/10 text-red-300 border-red-500/30"
                    }`} data-testid="zalo-probe-last-state">
                      <p className="font-medium">
                        {state.last_success ? "✅ Probe gần nhất: OK" : "❌ Probe gần nhất: FAIL"}
                        {" · "}
                        <span className="font-mono opacity-80">
                          {state.last_at ? new Date(state.last_at).toLocaleString("vi-VN", { hour12: false }) : "—"}
                        </span>
                      </p>
                      {state.last_phone && (
                        <p className="opacity-80 mt-0.5">SDT: {state.last_phone} · Template: {state.last_template_id || "—"}</p>
                      )}
                      {state.last_error && (
                        <p className="opacity-90 mt-0.5">Lỗi: {state.last_error}</p>
                      )}
                    </div>
                  );
                })()}
                <p className="text-[10px] text-slate-500 italic">
                  Cron tự động gửi 1 ZNS test (OTP="PROBE0") tới SDT theo chu kỳ <b>{notiSettings.zalo_probe_interval_hours || 8}</b> giờ, tối đa <b>{notiSettings.zalo_probe_max_per_day || 3}</b> lần/ngày. Nếu fail → Discord alert vào channel kế toán.
                  Sau khi đổi config, bấm "Lưu cài đặt" để áp dụng.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Access Token (paste để override thủ công)</Label>
                  <Input
                    value={notiSettings.zalo_access_token || ""}
                    onChange={(e) => setNotiSettings(s => ({ ...s, zalo_access_token: e.target.value }))}
                    placeholder="Paste Zalo OA Access Token..."
                    className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-600 focus:border-amber-500 font-mono text-xs"
                    data-testid="zalo-access-token-input"
                  />
                  <p className="text-[10px] text-slate-600">Access token hết hạn ~24-48h. Hệ thống tự refresh bằng refresh_token (có concurrency lock).</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Refresh Token (paste để override thủ công)</Label>
                  <Input
                    value={notiSettings.zalo_refresh_token || ""}
                    onChange={(e) => setNotiSettings(s => ({ ...s, zalo_refresh_token: e.target.value }))}
                    placeholder="Paste Zalo Refresh Token..."
                    className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-600 focus:border-amber-500 font-mono text-xs"
                    data-testid="zalo-refresh-token-input"
                  />
                  <p className="text-[10px] text-slate-600">Refresh token được rotate mỗi lần refresh thành công. UI tự động hiển thị token mới nhất từ DB.</p>
                </div>
              </div>
            </div>

            {/* ZNS Template IDs */}
            <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-5 space-y-4">
              <h3 className="text-sm font-semibold text-[#F8FAFC] flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-sky-400" />
                ZNS Template IDs
              </h3>
              <p className="text-xs text-slate-500">
                Nhập Template ID từ Zalo Dashboard. Khi chưa có Template ID, tin nhắn sẽ ở chế độ MOCK.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Nhắc gia hạn</Label>
                  <Input
                    value={notiSettings.zns_template_nhac_gia_han || ""}
                    onChange={(e) => setNotiSettings(s => ({ ...s, zns_template_nhac_gia_han: e.target.value }))}
                    placeholder="Template ID"
                    className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-600 focus:border-lime-500"
                    data-testid="zns-template-gia-han"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Xác nhận đặt sân</Label>
                  <Input
                    value={notiSettings.zns_template_xac_nhan_dat_san || ""}
                    onChange={(e) => setNotiSettings(s => ({ ...s, zns_template_xac_nhan_dat_san: e.target.value }))}
                    placeholder="Template ID"
                    className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-600 focus:border-lime-500"
                    data-testid="zns-template-xac-nhan"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Khuyến mãi</Label>
                  <Input
                    value={notiSettings.zns_template_khuyen_mai || ""}
                    onChange={(e) => setNotiSettings(s => ({ ...s, zns_template_khuyen_mai: e.target.value }))}
                    placeholder="Template ID"
                    className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-600 focus:border-lime-500"
                    data-testid="zns-template-khuyen-mai"
                  />
                </div>
              </div>
              {(!notiSettings.zns_template_nhac_gia_han && !notiSettings.zns_template_xac_nhan_dat_san && !notiSettings.zns_template_khuyen_mai) ? (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded p-2 flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-400">Chưa có Template ID - đang ở chế độ MOCK. Tin nhắn được log nhưng chưa gửi qua Zalo.</p>
                </div>
              ) : notiSettings.zalo_connected ? (
                <div className="bg-lime-500/10 border border-lime-500/20 rounded p-2 flex items-start gap-2">
                  <div className="w-3.5 h-3.5 rounded-full bg-lime-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-lime-400">Zalo ZNS sẵn sàng gửi thật</p>
                </div>
              ) : null}
            </div>

            {/* ZNS Đặt sân & Giải đấu */}
            <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-5 space-y-4">
              <h3 className="text-sm font-semibold text-[#F8FAFC] flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-teal-400" />
                ZNS Đặt sân Online & Giải đấu
              </h3>
              <p className="text-xs text-slate-500">Template ID cho tính năng đặt sân online và giải đấu.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">KH đặt sân — Chờ xác nhận</Label>
                  <Input value={notiSettings.zns_template_dat_san_cho_xn || ""}
                    onChange={(e) => setNotiSettings(s => ({ ...s, zns_template_dat_san_cho_xn: e.target.value }))}
                    placeholder="Template ID" className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-600 focus:border-teal-500" />
                  <p className="text-[10px] text-slate-600">Biến: customer_name, court_name, guest_count, booking_time</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Xác nhận đặt sân (CEO approve)</Label>
                  <Input value={notiSettings.zns_template_dat_san_xac_nhan || ""}
                    onChange={(e) => setNotiSettings(s => ({ ...s, zns_template_dat_san_xac_nhan: e.target.value }))}
                    placeholder="Template ID" className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-600 focus:border-teal-500" />
                  <p className="text-[10px] text-slate-600">Biến: customer_name, court_name, booking_time, transfer_amount, bank_info</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Xác nhận lệ phí giải đấu</Label>
                  <Input value={notiSettings.zns_template_le_phi_giai || ""}
                    onChange={(e) => setNotiSettings(s => ({ ...s, zns_template_le_phi_giai: e.target.value }))}
                    placeholder="Template ID" className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-600 focus:border-teal-500" />
                  <p className="text-[10px] text-slate-600">Biến: customer_name, partner_name, category, transfer_amount, contract_number</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">SĐT nhận thông báo (CEO/Staff)</Label>
                  <Input value={notiSettings.zns_sdt_nhan_thongbao || ""}
                    onChange={(e) => setNotiSettings(s => ({ ...s, zns_sdt_nhan_thongbao: e.target.value }))}
                    placeholder="0901234567, 0912345678" className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-600 focus:border-teal-500" />
                  <p className="text-[10px] text-slate-600">Nhiều SĐT cách nhau bằng dấu phẩy. Nhận thông báo khi KH đặt sân.</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">OTP xác nhận SĐT</Label>
                  <Input value={notiSettings.zns_template_otp || ""}
                    onChange={(e) => setNotiSettings(s => ({ ...s, zns_template_otp: e.target.value }))}
                    placeholder="Template ID" className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-600 focus:border-teal-500" />
                  <p className="text-[10px] text-slate-600">Biến: otp. Gửi mã xác thực khi KH đăng nhập Google/Apple.</p>
                </div>
              </div>
            </div>

            {/* SMS Config */}
            <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-5 space-y-4">
              <h3 className="text-sm font-semibold text-[#F8FAFC] flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-purple-400" />
                Cấu hình SMS
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Nhà cung cấp SMS</Label>
                  <Select value={notiSettings.sms_provider} onValueChange={(v) => setNotiSettings(s => ({ ...s, sms_provider: v }))}>
                    <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-50" data-testid="sms-provider-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1e293b] border-[#334155]">
                      <SelectItem value="esms" className="text-slate-200 focus:bg-[#334155]">eSMS</SelectItem>
                      <SelectItem value="stringee" className="text-slate-200 focus:bg-[#334155]">Stringee</SelectItem>
                      <SelectItem value="twilio" className="text-slate-200 focus:bg-[#334155]">Twilio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">SMS API Key</Label>
                  <div className="relative">
                    <Input
                      type={showKeys.sms_api_key ? "text" : "password"}
                      value={notiSettings.sms_api_key}
                      onChange={(e) => setNotiSettings(s => ({ ...s, sms_api_key: e.target.value }))}
                      placeholder="Nhập SMS API Key"
                      className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-600 focus:border-lime-500 pr-10"
                      data-testid="sms-api-key-input"
                    />
                    <button type="button" onClick={() => toggleShowKey("sms_api_key")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                      {showKeys.sms_api_key ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded p-2 flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-400">SMS đang ở chế độ MOCK - chưa tích hợp API thật.</p>
              </div>
            </div>

            {/* Auto-remind Config */}
            <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-5 space-y-4">
              <h3 className="text-sm font-semibold text-[#F8FAFC] flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-lime-400" />
                Tự động nhắc nhở
              </h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-200">Tự động nhắc thành viên sắp hết hạn</p>
                  <p className="text-xs text-slate-500 mt-0.5">Gửi thông báo Zalo/SMS khi thành viên gần hết hạn tập luyện</p>
                </div>
                <Switch
                  checked={notiSettings.auto_remind_expiring}
                  onCheckedChange={(v) => setNotiSettings(s => ({ ...s, auto_remind_expiring: v }))}
                  data-testid="auto-remind-toggle"
                />
              </div>
              {notiSettings.auto_remind_expiring && (
                <div className="space-y-2 pt-2 border-t border-[#334155]">
                  <Label className="text-slate-400 text-xs">Nhắc trước (ngày)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={30}
                    value={notiSettings.remind_days_before}
                    onChange={(e) => setNotiSettings(s => ({ ...s, remind_days_before: parseInt(e.target.value) || 7 }))}
                    className="bg-[#0f172a] border-[#334155] text-slate-50 focus:border-lime-500 w-24"
                    data-testid="remind-days-input"
                  />
                </div>
              )}
            </div>

            {/* CRM Giải đấu ZNS Config */}
            {tournaments.length > 0 && (
              <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-5 space-y-4">
                <h3 className="text-sm font-semibold text-[#F8FAFC] flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-amber-400" />
                  Cấu hình thông báo CRM giải đấu
                </h3>
                <p className="text-xs text-slate-500">ZNS Template ID để gửi thông báo lệ phí cho từng giải đấu.</p>
                <div className="space-y-3">
                  {tournaments.map(t => (
                    <div key={t.id} className="flex items-center gap-3 bg-[#0f172a] border border-[#334155] rounded-lg p-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-200 font-medium truncate">{t.name}</p>
                        <p className="text-[10px] text-slate-500">{t.participant_count} VĐV</p>
                      </div>
                      <div className="w-[200px]">
                        <Input
                          value={tournZnsConfigs[t.id]?.zns_template_id || ""}
                          onChange={(e) => setTournZnsConfigs(prev => ({ ...prev, [t.id]: { ...prev[t.id], zns_template_id: e.target.value } }))}
                          placeholder="ZNS Template ID"
                          className="bg-[#1e293b] border-[#334155] text-slate-50 text-xs h-8"
                          data-testid={`tourn-zns-${t.id}`}
                        />
                      </div>
                      <Button size="sm" onClick={() => handleSaveTournZns(t.id)} disabled={tournZnsSaving === t.id}
                        className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold text-xs h-8 gap-1 shrink-0"
                        data-testid={`tourn-zns-save-${t.id}`}>
                        {tournZnsSaving === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        Lưu
                      </Button>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-slate-600">
                  Biến ZNS: <code className="text-sky-400">customer_name</code> · <code className="text-sky-400">trinh_vdv</code> · <code className="text-sky-400">transfer_amount</code> · <code className="text-sky-400">contract_number</code> (tự sinh)
                </p>
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSaveNotiSettings}
                disabled={notiSaving}
                className="bg-lime-500 text-[#0f172a] font-semibold hover:bg-lime-400 gap-1.5"
                data-testid="save-noti-settings-btn"
              >
                <Save className="w-4 h-4" />
                {notiSaving ? "Đang lưu..." : "Lưu cấu hình"}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* KiotViet Import Tab */}
        <TabsContent value="kiotviet" className="mt-4">
          <KiotVietImportTab />
        </TabsContent>
        <TabsContent value="backup" className="mt-4">
          <AutoBackupTab />
        </TabsContent>
        <TabsContent value="zns-vars" className="mt-4">
          <ZnsVariablesTab />
        </TabsContent>
        <TabsContent value="theme-color" className="mt-4">
          <ThemeColorTab />
        </TabsContent>
        <TabsContent value="qrcode" className="mt-4">
          <QRCodeManager />
        </TabsContent>
        <TabsContent value="shortlink" className="mt-4">
          <ShortLinkManager />
        </TabsContent>
      </Tabs>
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] font-heading">Thêm người dùng mới</DialogTitle>
            <DialogDescription className="text-slate-400">Tạo tài khoản cho nhân viên hoặc khách hàng</DialogDescription>
          </DialogHeader>
          {userError && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2 text-sm text-red-400" data-testid="user-create-error">
              {userError}
            </div>
          )}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Họ tên</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-500 focus:border-lime-500"
                placeholder="Nhập họ tên"
                data-testid="new-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Email</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-500 focus:border-lime-500"
                placeholder="email@example.com"
                data-testid="new-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Mật khẩu</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-500 focus:border-lime-500"
                placeholder="Nhập mật khẩu"
                data-testid="new-user-password"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Số điện thoại</Label>
              <Input
                value={newUser.phone}
                onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-500 focus:border-lime-500"
                placeholder="0901234567"
                data-testid="new-user-phone"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Vai trò</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-50" data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1e293b] border-[#334155]">
                  <SelectItem value="staff" className="text-slate-200 focus:bg-[#334155]">Nhân viên (Staff)</SelectItem>
                  {isCeo && <SelectItem value="ceo" className="text-slate-200 focus:bg-[#334155]">CEO</SelectItem>}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUserDialogOpen(false)}
              className="border-[#334155] text-slate-300 hover:bg-[#334155]"
            >
              Huỷ
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={userSubmitting || !newUser.name || !newUser.email || !newUser.password}
              className="bg-lime-500 text-[#0f172a] font-semibold hover:bg-lime-400"
              data-testid="submit-create-user"
            >
              {userSubmitting ? "Đang tạo..." : "Tạo tài khoản"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Court Dialog */}
      <Dialog open={courtDialogOpen} onOpenChange={setCourtDialogOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] font-heading flex items-center gap-2">
              <Pencil className="w-4 h-4 text-lime-400" />
              Chỉnh sửa sân: {editingCourt?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">Cập nhật thông tin và giá cả sân</DialogDescription>
          </DialogHeader>
          {courtError && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2 text-sm text-red-400 flex items-center gap-2" data-testid="court-edit-error">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {courtError}
            </div>
          )}
          {editingCourt && (
            <div className="space-y-4">
              {/* Name & Description */}
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Tên sân</Label>
                <Input
                  value={editingCourt.name}
                  onChange={(e) => setEditingCourt({ ...editingCourt, name: e.target.value })}
                  className="bg-[#0f172a] border-[#334155] text-slate-50 focus:border-lime-500"
                  data-testid="edit-court-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Mô tả</Label>
                <Textarea
                  value={editingCourt.description || ""}
                  onChange={(e) => setEditingCourt({ ...editingCourt, description: e.target.value })}
                  placeholder="Mô tả ngắn về sân..."
                  rows={2}
                  className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-600 focus:border-lime-500 resize-none"
                  data-testid="edit-court-description"
                />
              </div>

              {/* Quick Price Summary */}
              <div className="bg-[#0f172a] rounded-lg p-4 border border-[#334155] space-y-3">
                <h4 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-lime-400" />
                  Giá tổng quan
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-emerald-400 text-xs flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      Giá cơ bản
                    </Label>
                    <Input
                      type="number"
                      value={editingCourt.base_price || 0}
                      onChange={(e) => setEditingCourt({ ...editingCourt, base_price: parseInt(e.target.value) || 0 })}
                      className="bg-[#1e293b] border-[#334155] text-slate-50 focus:border-emerald-500 h-9 text-sm"
                      data-testid="edit-court-base-price"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-orange-400 text-xs flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      Giá cao điểm
                    </Label>
                    <Input
                      type="number"
                      value={editingCourt.peak_price || 0}
                      onChange={(e) => setEditingCourt({ ...editingCourt, peak_price: parseInt(e.target.value) || 0 })}
                      className={`bg-[#1e293b] border-[#334155] text-slate-50 h-9 text-sm ${
                        editingCourt.peak_price < editingCourt.base_price
                          ? "border-red-500/50 focus:border-red-500"
                          : "focus:border-orange-500"
                      }`}
                      data-testid="edit-court-peak-price"
                    />
                    {editingCourt.peak_price < editingCourt.base_price && (
                      <p className="text-[10px] text-red-400">{"Phải >= giá cơ bản"}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-violet-400 text-xs flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Giá cuối tuần
                    </Label>
                    <Input
                      type="number"
                      value={editingCourt.weekend_price || 0}
                      onChange={(e) => setEditingCourt({ ...editingCourt, weekend_price: parseInt(e.target.value) || 0 })}
                      className={`bg-[#1e293b] border-[#334155] text-slate-50 h-9 text-sm ${
                        editingCourt.weekend_price < editingCourt.base_price
                          ? "border-red-500/50 focus:border-red-500"
                          : "focus:border-violet-500"
                      }`}
                      data-testid="edit-court-weekend-price"
                    />
                    {editingCourt.weekend_price < editingCourt.base_price && (
                      <p className="text-[10px] text-red-400">{"Phải >= giá cơ bản"}</p>
                    )}
                  </div>
                </div>
                {/* Quick price suggestion buttons */}
                <div className="flex flex-wrap gap-1.5 pt-2 border-t border-[#334155]/50">
                  <span className="text-[10px] text-slate-500 self-center mr-1">Gợi ý:</span>
                  {[100000, 130000, 150000, 180000, 200000, 250000].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setEditingCourt({ ...editingCourt, base_price: p })}
                      className="text-[10px] px-2 py-0.5 rounded-full bg-[#334155]/50 text-slate-400 hover:text-lime-400 hover:bg-lime-500/10 transition-colors"
                      data-testid={`suggest-price-${p}`}
                    >
                      {formatVND(p)}đ
                    </button>
                  ))}
                </div>
              </div>

              {/* Detailed time slot pricing */}
              {editingCourt.pricing?.map((dayPricing, dayIdx) => (
                <div key={dayIdx} className="space-y-2">
                  <h4 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5 text-slate-400" />
                    {dayPricing.day_type === "weekday" ? "Ngày thường" : "Cuối tuần"}
                  </h4>
                  <div className="space-y-2">
                    {dayPricing.time_slots?.map((slot, slotIdx) => (
                      <div key={slotIdx} className="flex items-center gap-3">
                        <span className="text-xs text-slate-500 w-24 shrink-0 font-mono">
                          {slot.start} - {slot.end}
                        </span>
                        <Input
                          type="number"
                          value={slot.price}
                          onChange={(e) => updateSlotPrice(dayIdx, slotIdx, e.target.value)}
                          className="bg-[#0f172a] border-[#334155] text-slate-50 focus:border-lime-500 h-9"
                          data-testid={`price-${dayIdx}-${slotIdx}`}
                        />
                        <span className="text-xs text-slate-500 shrink-0">VND</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCourtDialogOpen(false)}
              className="border-[#334155] text-slate-300 hover:bg-[#334155]"
            >
              Huỷ
            </Button>
            <Button
              onClick={handleUpdateCourt}
              disabled={courtSubmitting}
              className="bg-lime-500 text-[#0f172a] font-semibold hover:bg-lime-400 gap-1.5"
              data-testid="submit-update-court"
            >
              <Save className="w-4 h-4" />
              {courtSubmitting ? "Đang lưu..." : "Lưu thay đổi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test gửi ZNS dialog */}
      <Dialog open={zaloTestZnsOpen} onOpenChange={setZaloTestZnsOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-md" data-testid="zalo-test-zns-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] flex items-center gap-2">
              <Send className="w-5 h-5 text-cyan-400" />
              Test gửi ZNS thử
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">
              Gửi 1 OTP test đến SĐT bạn nhập để verify token + template hoạt động.
              OTP sẽ là <span className="text-cyan-400 font-mono">123456</span> (không lưu vào DB).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs">Số điện thoại nhận test</Label>
              <Input
                value={zaloTestPhone}
                onChange={(e) => setZaloTestPhone(e.target.value)}
                placeholder="0901234567"
                className="bg-[#0f172a] border-[#334155] text-slate-50 placeholder:text-slate-600 focus:border-cyan-500"
                data-testid="zalo-test-zns-phone-input"
              />
            </div>
            {zaloTestZnsResult && (
              <div className={`text-xs p-2 rounded border ${
                zaloTestZnsResult.success
                  ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                  : "bg-red-500/10 text-red-400 border-red-500/30"
              }`} data-testid="zalo-test-zns-result">
                <p className="font-medium">{zaloTestZnsResult.message}</p>
                {zaloTestZnsResult.error_code !== undefined && zaloTestZnsResult.error_code !== 0 && (
                  <p className="mt-1 text-[10px] opacity-80">Mã lỗi: {zaloTestZnsResult.error_code}</p>
                )}
                {zaloTestZnsResult.template_id && (
                  <p className="mt-1 text-[10px] opacity-80">Template: {zaloTestZnsResult.template_id}</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setZaloTestZnsOpen(false)} className="text-slate-400">Đóng</Button>
            <Button
              onClick={handleZaloTestZns}
              disabled={zaloTestZnsSending || !zaloTestPhone}
              className="bg-cyan-500 hover:bg-cyan-600 gap-1.5"
              data-testid="zalo-test-zns-send-btn"
            >
              {zaloTestZnsSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {zaloTestZnsSending ? "Đang gửi..." : "Gửi test"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chẩn đoán Zalo dialog */}
      <Dialog open={zaloDiagnoseOpen} onOpenChange={setZaloDiagnoseOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-2xl" data-testid="zalo-diagnose-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] flex items-center gap-2">
              <Code className="w-5 h-5 text-purple-400" />
              Chẩn đoán Zalo OAuth
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">
              Gọi raw API Zalo `/v4/oa/access_token` với credentials hiện tại để lấy response chính xác.
              Read-only — KHÔNG ảnh hưởng token đang dùng.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
            {zaloDiagnosing && (
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Đang gọi Zalo API...
              </div>
            )}
            {zaloDiagnoseResult && (
              <>
                {/* Interpretation */}
                <div className={`p-3 rounded border ${
                  zaloDiagnoseResult.ok
                    ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                    : "bg-red-500/10 text-red-300 border-red-500/30"
                }`} data-testid="zalo-diagnose-interpretation">
                  <p className="text-sm font-semibold">{zaloDiagnoseResult.ok ? "Kết luận" : "Phát hiện lỗi"}</p>
                  <p className="text-xs mt-1 leading-relaxed">{zaloDiagnoseResult.interpretation}</p>
                </div>

                {/* Request info */}
                {zaloDiagnoseResult.request && (
                  <div className="bg-[#0f172a] border border-[#334155] rounded p-3" data-testid="zalo-diagnose-request">
                    <p className="text-[10px] text-slate-500 uppercase font-medium mb-1.5">Request gửi đi</p>
                    <pre className="text-[11px] text-slate-300 font-mono whitespace-pre-wrap break-all">
{JSON.stringify(zaloDiagnoseResult.request, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Raw response */}
                {zaloDiagnoseResult.response && (
                  <div className="bg-[#0f172a] border border-[#334155] rounded p-3" data-testid="zalo-diagnose-response">
                    <p className="text-[10px] text-slate-500 uppercase font-medium mb-1.5">Response Zalo trả</p>
                    <pre className="text-[11px] text-slate-300 font-mono whitespace-pre-wrap break-all">
{JSON.stringify(zaloDiagnoseResult.response, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Action hints */}
                {!zaloDiagnoseResult.ok && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 text-[11px] text-amber-200">
                    💡 Sau khi fix xong (paste secret/refresh_token mới + Lưu), bấm <strong>Refresh token ngay</strong> hoặc <strong>Reset circuit</strong> để kích hoạt lại auto-refresh.
                  </div>
                )}
              </>
            )}

            {/* History từ auto-refresh / cron — admin trace token nào đang invalid */}
            {zaloRefreshHistory && (zaloRefreshHistory.last_refresh_at || (zaloRefreshHistory.history || []).length > 0) && (
              <div className="border-t border-[#334155] pt-3 space-y-2" data-testid="zalo-refresh-history-section">
                <p className="text-[11px] text-slate-300 uppercase font-semibold flex items-center gap-1.5">
                  <History className="w-3 h-3" /> Lịch sử refresh tự động (auto/cron)
                </p>

                {/* Last refresh: request + response */}
                {zaloRefreshHistory.last_refresh_request && (
                  <>
                    <div className="bg-[#0f172a] border border-[#334155] rounded p-2.5">
                      <p className="text-[10px] text-slate-500 uppercase mb-1">
                        Lần refresh gần nhất — {zaloRefreshHistory.last_refresh_at
                          ? new Date(zaloRefreshHistory.last_refresh_at).toLocaleString("vi-VN", { hour12: false })
                          : "—"}
                        {" · "}
                        <span className={zaloRefreshHistory.last_refresh_success ? "text-emerald-400" : "text-red-400"}>
                          {zaloRefreshHistory.last_refresh_success ? "OK" : "FAIL"}
                        </span>
                        {zaloRefreshHistory.last_refresh_rotated && (
                          <span className="text-cyan-400 ml-2">· rotated ✓</span>
                        )}
                      </p>
                      {zaloRefreshHistory.last_refresh_error_summary && (
                        <p className="text-[10px] text-red-300 mb-1">{zaloRefreshHistory.last_refresh_error_summary}</p>
                      )}
                      <p className="text-[10px] text-slate-500 uppercase mt-1.5">Request</p>
                      <pre className="text-[10px] text-slate-400 font-mono whitespace-pre-wrap break-all">
{JSON.stringify(zaloRefreshHistory.last_refresh_request, null, 2)}
                      </pre>
                      <p className="text-[10px] text-slate-500 uppercase mt-1.5">Response</p>
                      <pre className="text-[10px] text-slate-400 font-mono whitespace-pre-wrap break-all">
{JSON.stringify(zaloRefreshHistory.last_refresh_response, null, 2)}
                      </pre>
                    </div>
                  </>
                )}

                {/* Compact history list */}
                {(zaloRefreshHistory.history || []).length > 0 && (
                  <div className="space-y-1">
                    <p className="text-[10px] text-slate-500 uppercase">10 lần gần nhất</p>
                    {[...(zaloRefreshHistory.history || [])].reverse().map((h, i) => (
                      <div
                        key={i}
                        className={`text-[10px] font-mono p-1.5 rounded border ${
                          h.success
                            ? "bg-emerald-500/5 border-emerald-500/20 text-emerald-300"
                            : "bg-red-500/5 border-red-500/20 text-red-300"
                        }`}
                        data-testid={`zalo-history-item-${i}`}
                      >
                        <span className="opacity-60">
                          {h.at ? new Date(h.at).toLocaleString("vi-VN", { hour12: false }) : "—"}
                        </span>
                        {" · "}
                        <span>{h.success ? "OK" : "FAIL"}</span>
                        {" · req refresh="}
                        <span className="opacity-80">{h.request?.refresh_token_preview || "—"}</span>
                        {h.response?.body?.error !== undefined && (
                          <>
                            {" · resp error="}
                            <span>{h.response.body.error}</span>
                            {h.response.body.error_name && <> {h.response.body.error_name}</>}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setZaloDiagnoseOpen(false)} className="text-slate-400">Đóng</Button>
            <Button
              onClick={handleZaloDiagnose}
              disabled={zaloDiagnosing}
              className="bg-purple-500 hover:bg-purple-600 gap-1.5"
              data-testid="zalo-diagnose-rerun-btn"
            >
              {zaloDiagnosing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Chạy lại
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// --- KiotViet Import Tab ---
function KiotVietImportTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Coffee import state
  const [coffeeImporting, setCoffeeImporting] = useState(false);
  const [coffeeDeleting, setCoffeeDeleting] = useState(false);
  const [confirmDeleteCoffee, setConfirmDeleteCoffee] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/import/status");
      setStatus(data);
    } catch (err) {
      toast.error("Không thể kiểm tra trạng thái import");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleImportAll = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/import/all");
      if (data.already_imported) {
        toast.error(data.message);
      } else {
        toast.success(data.message);
      }
      fetchStatus();
    } catch (err) {
      toast.error("Lỗi import: " + (err.response?.data?.detail || "Unknown"));
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await api.delete("/import/sample-data");
      toast.success(data.message);
      setConfirmDelete(false);
      fetchStatus();
    } catch (err) {
      toast.error("Lỗi xoá: " + (err.response?.data?.detail || "Unknown"));
    } finally {
      setDeleting(false);
    }
  };

  const handleImportCoffee = async () => {
    setCoffeeImporting(true);
    try {
      const [custRes, prodRes] = await Promise.all([
        api.post("/import/customers-coffee"),
        api.post("/import/products-coffee"),
      ]);
      toast.success(`${custRes.data.message} | ${prodRes.data.message}`);
      fetchStatus();
    } catch (err) {
      toast.error("Lỗi import Coffee: " + (err.response?.data?.detail || "Unknown"));
    } finally {
      setCoffeeImporting(false);
    }
  };

  const handleDeleteCoffee = async () => {
    setCoffeeDeleting(true);
    try {
      const { data } = await api.delete("/import/coffee-data");
      toast.success(data.message);
      setConfirmDeleteCoffee(false);
      fetchStatus();
    } catch (err) {
      toast.error("Lỗi xoá Coffee: " + (err.response?.data?.detail || "Unknown"));
    } finally {
      setCoffeeDeleting(false);
    }
  };

  const items = [
    { key: "products", label: "Sản phẩm", icon: "box" },
    { key: "categories", label: "Danh mục", icon: "folder" },
    { key: "recipes", label: "Công thức", icon: "flask" },
    { key: "tables", label: "Phòng/Bàn", icon: "layout" },
    { key: "invoices", label: "Hoá đơn", icon: "receipt" },
    { key: "cashbook", label: "Sổ quỹ", icon: "wallet" },
    { key: "goods_import", label: "Nhập hàng", icon: "upload" },
    { key: "goods_return", label: "Trả hàng", icon: "undo" },
    { key: "goods_dispose", label: "Xuất huỷ", icon: "trash" },
  ];

  return (
    <div className="space-y-4" data-testid="kiotviet-import-tab">
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-bold text-[#F8FAFC] flex items-center gap-2">
              <Upload className="w-4 h-4 text-lime-400" />
              Import dữ liệu KiotViet
            </h3>
            <p className="text-xs text-slate-500 mt-1">Import sản phẩm, phòng/bàn, hoá đơn, sổ quỹ, công thức, nhập hàng, trả hàng, xuất huỷ từ file Excel KiotViet</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleImportAll}
              disabled={importing || (status?.imported)}
              className="bg-lime-500 text-[#0f172a] font-semibold hover:bg-lime-400"
              data-testid="import-all-btn"
            >
              {importing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Đang import...</> : <><FileUp className="w-4 h-4 mr-1.5" />Import tất cả</>}
            </Button>
            {status?.imported && (
              <Button
                onClick={() => setConfirmDelete(true)}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                data-testid="delete-sample-btn"
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                Xoá dữ liệu mẫu
              </Button>
            )}
          </div>
        </div>

        {/* Status grid */}
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
          {items.map(item => (
            <div key={item.key} className="bg-[#0f172a] border border-[#334155] rounded-lg p-3 text-center">
              <div className={`text-xl font-bold ${status?.[item.key] > 0 ? "text-lime-400" : "text-slate-600"}`}>
                {loading ? "..." : (status?.[item.key] || 0).toLocaleString("vi-VN")}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">{item.label}</div>
            </div>
          ))}
        </div>

        {status?.imported && (
          <div className="mt-3 flex items-center gap-2 text-xs text-lime-400 bg-lime-500/5 border border-lime-500/20 rounded-lg px-3 py-2">
            <Activity className="w-3.5 h-3.5" />
            Đã import {status.total?.toLocaleString("vi-VN")} bản ghi từ KiotViet (dữ liệu mẫu — có thể xoá)
          </div>
        )}
      </div>

      {/* Coffee Data Section */}
      <div className="bg-[#1e293b] border border-purple-500/20 rounded-xl p-5" data-testid="coffee-import-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-bold text-[#F8FAFC] flex items-center gap-2">
              <FileUp className="w-4 h-4 text-purple-400" />
              Dữ liệu Coffee (KiotViet)
            </h3>
            <p className="text-xs text-slate-500 mt-1">Import khách hàng + sản phẩm Cafe từ file Excel KiotViet</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleImportCoffee}
              disabled={coffeeImporting}
              className="bg-purple-500 hover:bg-purple-400 text-white font-semibold"
              data-testid="import-coffee-btn"
            >
              {coffeeImporting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Đang import...</> : <><FileUp className="w-4 h-4 mr-1.5" />Import Coffee</>}
            </Button>
            {status?.coffee_imported && (
              <Button
                onClick={() => setConfirmDeleteCoffee(true)}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                data-testid="delete-coffee-btn"
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                Xoá dữ liệu Coffee
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-4 text-center">
            <div className={`text-2xl font-bold ${status?.coffee_customers > 0 ? "text-purple-400" : "text-slate-600"}`}>
              {loading ? "..." : (status?.coffee_customers || 0).toLocaleString("vi-VN")}
            </div>
            <div className="text-xs text-slate-500 mt-1">Khách hàng Coffee</div>
          </div>
          <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-4 text-center">
            <div className={`text-2xl font-bold ${status?.coffee_products > 0 ? "text-purple-400" : "text-slate-600"}`}>
              {loading ? "..." : (status?.coffee_products || 0).toLocaleString("vi-VN")}
            </div>
            <div className="text-xs text-slate-500 mt-1">Sản phẩm Coffee</div>
          </div>
        </div>

        {status?.coffee_imported && (
          <div className="mt-3 flex items-center gap-2 text-xs text-purple-400 bg-purple-500/5 border border-purple-500/20 rounded-lg px-3 py-2">
            <Activity className="w-3.5 h-3.5" />
            Đã import {((status.coffee_customers || 0) + (status.coffee_products || 0)).toLocaleString("vi-VN")} bản ghi Coffee (có thể xoá bằng nút "Xoá dữ liệu Coffee")
          </div>
        )}
      </div>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Xác nhận xoá
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Xoá toàn bộ {status?.total?.toLocaleString("vi-VN")} bản ghi mẫu KiotViet? Thao tác này không thể hoàn tác.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)} className="text-slate-400">Huỷ</Button>
            <Button onClick={handleDelete} disabled={deleting} className="bg-red-500 text-white hover:bg-red-600">
              {deleting ? "Đang xoá..." : "Xoá dữ liệu mẫu"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Coffee Dialog */}
      <Dialog open={confirmDeleteCoffee} onOpenChange={setConfirmDeleteCoffee}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Xác nhận xoá dữ liệu Coffee
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Xoá {(status?.coffee_customers || 0).toLocaleString("vi-VN")} khách hàng + {(status?.coffee_products || 0).toLocaleString("vi-VN")} sản phẩm Coffee? Thao tác không thể hoàn tác.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDeleteCoffee(false)} className="text-slate-400">Huỷ</Button>
            <Button onClick={handleDeleteCoffee} disabled={coffeeDeleting} className="bg-red-500 text-white hover:bg-red-600" data-testid="confirm-delete-coffee-btn">
              {coffeeDeleting ? "Đang xoá..." : "Xoá dữ liệu Coffee"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Auto Backup Tab ---
const BACKUP_COLLECTIONS = [
  { key: "customers", label: "Khách hàng" },
  { key: "bookings", label: "Lịch đặt sân" },
  { key: "pos_orders", label: "Đơn POS" },
  { key: "pos_products", label: "Sản phẩm" },
  { key: "pos_payments", label: "Thanh toán" },
  { key: "tickets", label: "Vé QR" },
  { key: "groups", label: "Quản lý hội" },
  { key: "invoices", label: "Hoá đơn KV" },
  { key: "cashbook", label: "Sổ quỹ KV" },
  { key: "notifications", label: "Thông báo" },
];

function AutoBackupTab() {
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [backing, setBacking] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, histRes] = await Promise.all([
        api.get("/backup/config"),
        api.get("/backup/history"),
      ]);
      setConfig(cfgRes.data);
      setHistory(histRes.data.backups || []);
    } catch { toast.error("Lỗi tải cấu hình backup"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await api.put("/backup/config", config);
      toast.success("Đã lưu cấu hình backup");
    } catch (err) { toast.error("Lỗi lưu"); }
    finally { setSaving(false); }
  };

  const handleBackupNow = async () => {
    setBacking(true);
    try {
      const { data } = await api.post("/backup/run");
      toast.success(data.message);
      fetchData();
    } catch (err) { toast.error("Lỗi backup"); }
    finally { setBacking(false); }
  };

  const handleDeleteBackup = async (id) => {
    try {
      await api.delete(`/backup/${id}`);
      toast.success("Đã xoá backup");
      fetchData();
    } catch { toast.error("Lỗi"); }
  };

  const toggleCollection = (key) => {
    if (!config) return;
    const cols = config.collections || [];
    setConfig({
      ...config,
      collections: cols.includes(key) ? cols.filter(c => c !== key) : [...cols, key],
    });
  };

  if (loading || !config) return <div className="text-slate-500 text-center py-8">Đang tải...</div>;

  return (
    <div className="space-y-4" data-testid="backup-tab">
      {/* Config panel */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-lime-400" />
            <h3 className="text-sm font-bold text-[#F8FAFC]">Cấu hình Auto Backup</h3>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={config.enabled} onCheckedChange={v => setConfig({...config, enabled: v})} />
            <span className="text-xs text-slate-400">{config.enabled ? "Bật" : "Tắt"}</span>
          </div>
        </div>

        {/* Schedule */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <Label className="text-slate-300 text-xs mb-1 block">Tần suất</Label>
            <select value={config.schedule_type} onChange={e => setConfig({...config, schedule_type: e.target.value})}
              className="w-full bg-[#0f172a] border border-[#334155] rounded-md px-2 py-2 text-xs text-slate-200">
              <option value="hourly">Mỗi giờ</option>
              <option value="daily">Mỗi ngày</option>
              <option value="weekly">Mỗi tuần</option>
              <option value="monthly">Mỗi tháng</option>
            </select>
          </div>
          <div>
            <Label className="text-slate-300 text-xs mb-1 block">Vào lúc (giờ)</Label>
            <select value={config.schedule_hour} onChange={e => setConfig({...config, schedule_hour: parseInt(e.target.value)})}
              className="w-full bg-[#0f172a] border border-[#334155] rounded-md px-2 py-2 text-xs text-slate-200">
              {Array.from({length: 24}, (_, i) => <option key={i} value={i}>{String(i).padStart(2,'0')}:00</option>)}
            </select>
          </div>
          {(config.schedule_type === "weekly" || config.schedule_type === "monthly") && (
            <div>
              <Label className="text-slate-300 text-xs mb-1 block">{config.schedule_type === "weekly" ? "Thứ" : "Ngày"}</Label>
              <Input type="number" min={1} max={config.schedule_type === "weekly" ? 7 : 31}
                value={config.schedule_day} onChange={e => setConfig({...config, schedule_day: parseInt(e.target.value) || 1})}
                className="bg-[#0f172a] border-[#334155] text-slate-50 h-9" />
            </div>
          )}
          <div>
            <Label className="text-slate-300 text-xs mb-1 block">Giữ (ngày)</Label>
            <Input type="number" min={1} max={90} value={config.retention_days}
              onChange={e => setConfig({...config, retention_days: parseInt(e.target.value) || 30})}
              className="bg-[#0f172a] border-[#334155] text-slate-50 h-9" />
          </div>
        </div>

        {/* Collections checkboxes */}
        <div>
          <Label className="text-slate-300 text-xs mb-2 block">Dữ liệu cần backup</Label>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {BACKUP_COLLECTIONS.map(c => (
              <label key={c.key} className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={(config.collections || []).includes(c.key)}
                  onChange={() => toggleCollection(c.key)} className="rounded border-slate-500 w-3 h-3" />
                <span className="text-[10px] text-slate-300">{c.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Smart backup toggles */}
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch checked={config.before_change_backup} onCheckedChange={v => setConfig({...config, before_change_backup: v})} />
            <div>
              <span className="text-xs text-slate-200 font-medium">Before-Change Backup</span>
              <p className="text-[10px] text-slate-500">Tự động backup trước import/xoá hàng loạt</p>
            </div>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch checked={config.critical_event_backup} onCheckedChange={v => setConfig({...config, critical_event_backup: v})} />
            <div>
              <span className="text-xs text-slate-200 font-medium">Critical Event Backup</span>
              <p className="text-[10px] text-slate-500">Backup khi phát hiện thay đổi bất thường ({config.critical_threshold}+ records/phút)</p>
            </div>
          </label>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2 border-t border-[#334155]">
          <Button onClick={handleSave} disabled={saving} className="bg-lime-500 text-[#0f172a] font-semibold" data-testid="save-backup-config">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Lưu cấu hình
          </Button>
          <Button onClick={handleBackupNow} disabled={backing} variant="outline" className="border-[#334155] text-slate-300" data-testid="backup-now-btn">
            {backing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
            Backup ngay
          </Button>
        </div>
      </div>

      {/* History */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#334155] flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#F8FAFC] flex items-center gap-2">
            <History className="w-4 h-4 text-lime-400" /> Lịch sử Backup ({history.length})
          </h3>
        </div>
        <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[#0f172a]">
              <tr className="text-slate-400">
                <th className="px-3 py-2 text-left">Thời gian</th>
                <th className="px-3 py-2 text-left">Loại</th>
                <th className="px-3 py-2 text-center">Records</th>
                <th className="px-3 py-2 text-right">Dung lượng</th>
                <th className="px-3 py-2 text-center">Thời gian</th>
                <th className="px-3 py-2 text-center w-20">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500">Chưa có backup</td></tr> :
              history.map(b => (
                <tr key={b.id} className="border-t border-[#334155]/30 hover:bg-[#334155]/10">
                  <td className="px-3 py-2 text-slate-300">{b.created_at ? new Date(b.created_at).toLocaleString("vi-VN") : "—"}</td>
                  <td className="px-3 py-2">
                    <Badge className={`text-[9px] border-0 ${
                      b.trigger === "manual" ? "bg-lime-500/20 text-lime-400" :
                      b.trigger === "scheduled" ? "bg-sky-500/20 text-sky-400" :
                      b.trigger?.includes("critical") ? "bg-red-500/20 text-red-400" :
                      "bg-amber-500/20 text-amber-400"
                    }`}>{b.trigger}</Badge>
                  </td>
                  <td className="px-3 py-2 text-center text-slate-200 font-medium">{b.records_count?.toLocaleString("vi-VN")}</td>
                  <td className="px-3 py-2 text-right text-slate-400">{b.file_size_mb}MB</td>
                  <td className="px-3 py-2 text-center text-slate-400">{b.duration_sec}s</td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <a href={`${api.defaults.baseURL}/backup/download/${b.id}`} target="_blank" rel="noreferrer"
                        className="text-lime-400 hover:text-lime-300 p-1"><Download className="w-3.5 h-3.5" /></a>
                      <button onClick={() => handleDeleteBackup(b.id)} className="text-slate-400 hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quản lý dữ liệu mẫu */}
      <DataManagementSection />
    </div>
  );
}

function DataManagementSection() {
  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [action, setAction] = useState("");
  const [processing, setProcessing] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const handleAction = async () => {
    if (pin !== "6789") { toast.error("Mã PIN không đúng"); return; }
    setProcessing(true);
    try {
      let endpoint = "";
      if (action === "clear_sample") endpoint = "/data-management/clear-sample";
      else if (action === "clear_all_sample") endpoint = "/data-management/clear-all-sample";
      const { data } = await api.post(endpoint, { pin: "6789" });
      toast.success(data.message);
      setPinOpen(false); setPin("");
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
    finally { setProcessing(false); }
  };

  const handleSeedSample = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/data-management/seed-sample");
      toast.success(data.message);
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
    finally { setSeeding(false); }
  };

  return (
    <div className="bg-[#1e293b] border border-red-500/20 rounded-xl p-4 space-y-3" data-testid="data-management">
      <h3 className="text-sm font-bold text-red-400 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Quản lý dữ liệu</h3>
      <p className="text-[10px] text-slate-500">Các thao tác bên dưới KHÔNG ảnh hưởng: Khách hàng, Hội, Thành viên hội, Lịch sân, Giải đấu.</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-3 space-y-2">
          <p className="text-[10px] font-bold text-slate-300">Xoá dữ liệu mẫu</p>
          <p className="text-[9px] text-slate-500">Xoá dữ liệu Thống kê, AI Dashboard, AI Copilot mẫu</p>
          <Button onClick={() => { setAction("clear_sample"); setPinOpen(true); }} variant="outline" size="sm" className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 text-[10px] h-7" data-testid="clear-sample-btn">Xoá dữ liệu mẫu</Button>
        </div>
        <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-3 space-y-2">
          <p className="text-[10px] font-bold text-slate-300">Nhập dữ liệu mẫu</p>
          <p className="text-[9px] text-slate-500">Tạo mock data cho Thống kê, AI Dashboard, Copilot</p>
          <Button onClick={handleSeedSample} disabled={seeding} className="w-full bg-indigo-500 hover:bg-indigo-400 text-white text-[10px] h-7" data-testid="seed-sample-btn">
            {seeding ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Nhập dữ liệu mẫu
          </Button>
        </div>
        <div className="bg-[#0f172a] border border-red-500/10 rounded-lg p-3 space-y-2">
          <p className="text-[10px] font-bold text-red-300">Xoá toàn bộ dữ liệu mẫu</p>
          <p className="text-[9px] text-slate-500">Xoá TẤT CẢ dữ liệu mẫu (Kế toán, POS, Finance, Analytics...)</p>
          <Button onClick={() => { setAction("clear_all_sample"); setPinOpen(true); }} variant="outline" size="sm" className="w-full border-red-500/40 text-red-400 hover:bg-red-500/10 text-[10px] h-7" data-testid="clear-all-btn">Xoá toàn bộ</Button>
        </div>
      </div>

      <Dialog open={pinOpen} onOpenChange={setPinOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] max-w-xs">
          <DialogHeader><DialogTitle className="text-red-400 text-sm">Nhập PIN xác nhận</DialogTitle></DialogHeader>
          <Input value={pin} onChange={e => setPin(e.target.value)} placeholder="Nhập PIN..." type="password" maxLength={4} className="bg-[#0f172a] border-[#334155] text-slate-200 h-8 text-center tracking-widest" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPinOpen(false)} className="text-slate-400">Hủy</Button>
            <Button onClick={handleAction} disabled={processing || pin.length < 4} className="bg-red-500 text-white">
              {processing ? "Đang xoá..." : "Xác nhận"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function ZnsVariablesTab() {
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [editingVar, setEditingVar] = useState(null);
  const [newVar, setNewVar] = useState({ name: "", sample_value: "", description: "" });
  const [adding, setAdding] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchVars = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/zns-variables");
      setVariables(data.variables || []);
    } catch { toast.error("Lỗi tải biến ZNS"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchVars(); }, [fetchVars]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/zns-variables/seed");
      toast.success(data.message);
      fetchVars();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi seed"); }
    finally { setSeeding(false); }
  };

  const handleAdd = async () => {
    if (!newVar.name.trim()) { toast.error("Vui lòng nhập tên biến"); return; }
    // Check duplicate
    const dup = variables.find(v => v.name === newVar.name.trim());
    if (dup) {
      toast.error(`Biến "${newVar.name}" đã tồn tại! Vui lòng đổi tên hoặc sửa biến cũ.`);
      return;
    }
    setAdding(true);
    try {
      const { data } = await api.post("/zns-variables", newVar);
      toast.success(data.message);
      setNewVar({ name: "", sample_value: "", description: "" });
      setShowAddForm(false);
      fetchVars();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi tạo biến"); }
    finally { setAdding(false); }
  };

  const handleUpdate = async (v) => {
    try {
      await api.put(`/zns-variables/${v.id}`, {
        name: v.name, sample_value: v.sample_value, description: v.description,
      });
      toast.success(`Đã cập nhật biến "${v.name}"`);
      setEditingVar(null);
      fetchVars();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi cập nhật"); }
  };

  const handleDelete = async (v) => {
    if (!window.confirm(`Xoá biến "${v.name}"?`)) return;
    try {
      await api.delete(`/zns-variables/${v.id}`);
      toast.success(`Đã xoá biến "${v.name}"`);
      fetchVars();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi xoá"); }
  };

  return (
    <div className="space-y-4" data-testid="zns-variables-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-[#F8FAFC]">Cấu hình biến ZNS</h3>
          <p className="text-xs text-slate-400 mt-0.5">Quản lý các biến dùng trong ZNS templates gửi Zalo cho khách hàng</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSeed} disabled={seeding}
            className="bg-cyan-500 hover:bg-cyan-400 text-[#0f172a] text-xs gap-1" data-testid="seed-zns-vars-btn">
            <RefreshCw className={`w-3 h-3 ${seeding ? "animate-spin" : ""}`} />
            {seeding ? "Đang seed..." : "Seed mặc định"}
          </Button>
          <Button size="sm" onClick={() => setShowAddForm(!showAddForm)}
            className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] text-xs gap-1" data-testid="add-zns-var-btn">
            <Plus className="w-3 h-3" /> Thêm biến
          </Button>
        </div>
      </div>

      {/* Add form */}
      {showAddForm && (
        <div className="bg-[#1e293b] border border-lime-500/20 rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] text-slate-400 mb-1 block">Tên biến (VD: customer_name)</label>
              <Input value={newVar.name} onChange={(e) => setNewVar(p => ({ ...p, name: e.target.value }))}
                placeholder="ten_bien" className="bg-[#0f172a] border-[#334155] text-slate-200 h-8 text-xs font-mono" data-testid="new-var-name" />
            </div>
            <div>
              <label className="text-[10px] text-slate-400 mb-1 block">Giá trị mẫu</label>
              <Input value={newVar.sample_value} onChange={(e) => setNewVar(p => ({ ...p, sample_value: e.target.value }))}
                placeholder="VD: Nguyễn Văn A" className="bg-[#0f172a] border-[#334155] text-slate-200 h-8 text-xs" data-testid="new-var-sample" />
            </div>
            <div>
              <label className="text-[10px] text-slate-400 mb-1 block">Mô tả</label>
              <Input value={newVar.description} onChange={(e) => setNewVar(p => ({ ...p, description: e.target.value }))}
                placeholder="Mô tả biến..." className="bg-[#0f172a] border-[#334155] text-slate-200 h-8 text-xs" data-testid="new-var-desc" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setShowAddForm(false)} className="text-slate-400 text-xs">Hủy</Button>
            <Button size="sm" onClick={handleAdd} disabled={adding}
              className="bg-lime-500 text-[#0f172a] text-xs" data-testid="save-new-var-btn">
              {adding ? "Đang tạo..." : "Tạo biến"}
            </Button>
          </div>
        </div>
      )}

      {/* Variables table */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-[#0f172a]">
            <tr className="text-slate-400">
              <th className="px-3 py-2.5 text-left font-medium">Tên biến</th>
              <th className="px-3 py-2.5 text-left font-medium">Cú pháp ZNS</th>
              <th className="px-3 py-2.5 text-left font-medium">Giá trị mẫu</th>
              <th className="px-3 py-2.5 text-left font-medium">Mô tả</th>
              <th className="px-3 py-2.5 text-center font-medium w-20"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></td></tr>
            ) : variables.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">Chưa có biến ZNS. Nhấn "Seed mặc định" để tạo.</td></tr>
            ) : variables.map(v => (
              <tr key={v.id} className="border-t border-[#334155]/30 hover:bg-[#334155]/10" data-testid={`zns-var-${v.name}`}>
                <td className="px-3 py-2">
                  {editingVar?.id === v.id ? (
                    <Input value={editingVar.name} onChange={(e) => setEditingVar(p => ({ ...p, name: e.target.value }))}
                      className="bg-[#0f172a] border-[#334155] text-slate-200 h-7 text-xs font-mono" />
                  ) : (
                    <span className="text-slate-200 font-mono font-medium">{v.name}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <code className="text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded text-[10px]">&lt;{v.name}&gt;</code>
                </td>
                <td className="px-3 py-2">
                  {editingVar?.id === v.id ? (
                    <Input value={editingVar.sample_value} onChange={(e) => setEditingVar(p => ({ ...p, sample_value: e.target.value }))}
                      className="bg-[#0f172a] border-[#334155] text-slate-200 h-7 text-xs" />
                  ) : (
                    <span className="text-lime-400">{v.sample_value}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {editingVar?.id === v.id ? (
                    <Input value={editingVar.description} onChange={(e) => setEditingVar(p => ({ ...p, description: e.target.value }))}
                      className="bg-[#0f172a] border-[#334155] text-slate-200 h-7 text-xs" />
                  ) : (
                    <span className="text-slate-400">{v.description}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  {editingVar?.id === v.id ? (
                    <div className="flex gap-1 justify-center">
                      <Button size="sm" onClick={() => handleUpdate(editingVar)}
                        className="bg-lime-500 text-[#0f172a] h-6 text-[10px] px-2"><Save className="w-3 h-3" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingVar(null)}
                        className="text-slate-400 h-6 text-[10px] px-2">Hủy</Button>
                    </div>
                  ) : (
                    <div className="flex gap-1 justify-center">
                      <button onClick={() => setEditingVar({ ...v })} className="text-slate-400 hover:text-lime-400 p-1"><Pencil className="w-3 h-3" /></button>
                      <button onClick={() => handleDelete(v)} className="text-slate-400 hover:text-red-400 p-1"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ZNS Templates preview */}
      <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-4">
        <h4 className="text-xs font-bold text-[#F8FAFC] mb-2">Mẫu ZNS đã duyệt</h4>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="bg-[#1e293b] border border-sky-500/20 rounded-lg p-3">
            <div className="text-[10px] font-bold text-sky-400 mb-1.5">Đặt sân - Chờ duyệt</div>
            <div className="text-[10px] text-slate-300 whitespace-pre-wrap leading-relaxed">
{`Xin chào quý khách <customer_name>,
Cảm ơn quý khách đã đặt sân tại Karinox!
Mã giao dịch <receipt_id> đã ở trạng thái chờ duyệt.

Vị trí sân: <court_name>
Số lượng: <guest_count>
Khung giờ: <booking_time>
Mã giao dịch: <receipt_id>`}
            </div>
          </div>
          <div className="bg-[#1e293b] border border-amber-500/20 rounded-lg p-3">
            <div className="text-[10px] font-bold text-amber-400 mb-1.5">Thông tin thanh toán</div>
            <div className="text-[10px] text-slate-300 whitespace-pre-wrap leading-relaxed">
{`Cảm ơn quý khách <customer_name>, mã GD: <receipt_id>.
Vui lòng thanh toán:

Vị trí sân: <court_name>
Thời gian: <booking_time>
Số tiền: <price>
Mã giao dịch: <receipt_id>
Số tiền TT: <transfer_amount>đ

Vietcombank: 7935511678 - BÙI THANH KIỆT`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function ThemeColorTab() {
  const { theme, toggleTheme, primaryColor, primaryColorDark, setColor, COLOR_PRESETS } = useTheme();
  const [customHex, setCustomHex] = useState(primaryColor);
  const [customDarkHex, setCustomDarkHex] = useState(primaryColorDark);

  const handlePreset = (preset) => {
    setColor(preset.hex, preset.dark);
    setCustomHex(preset.hex);
    setCustomDarkHex(preset.dark);
    toast.success(`Đã đổi sang "${preset.name}"`);
  };

  const handleCustomApply = () => {
    if (!/^#[0-9a-fA-F]{6}$/.test(customHex)) {
      toast.error("Mã màu light không hợp lệ (VD: #605dff)");
      return;
    }
    if (!/^#[0-9a-fA-F]{6}$/.test(customDarkHex)) {
      toast.error("Mã màu dark không hợp lệ (VD: #a78bfa)");
      return;
    }
    setColor(customHex, customDarkHex);
    toast.success("Đã áp dụng màu tuỳ chỉnh");
  };

  const activePreset = COLOR_PRESETS.find(p => p.hex === primaryColor);

  return (
    <div className="space-y-6" data-testid="theme-color-tab">
      {/* Theme mode toggle */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-5">
        <h3 className="text-sm font-bold text-[#F8FAFC] mb-3">Chế độ giao diện</h3>
        <div className="flex items-center gap-4">
          <button
            onClick={() => { if (theme !== "dark") toggleTheme(); }}
            className={`flex-1 p-4 rounded-lg border-2 transition-all ${theme === "dark" ? "border-lime-500 bg-[#0f172a]" : "border-[#334155] bg-[#0f172a]/50 opacity-60 hover:opacity-80"}`}
            data-testid="theme-select-dark"
          >
            <div className="w-full h-16 bg-[#0f172a] rounded-md border border-[#334155] mb-2 flex items-center justify-center">
              <div className="w-8 h-2 rounded bg-lime-500"></div>
            </div>
            <p className="text-xs text-center text-slate-300 font-medium">Dark mode</p>
          </button>
          <button
            onClick={() => { if (theme !== "light") toggleTheme(); }}
            className={`flex-1 p-4 rounded-lg border-2 transition-all ${theme === "light" ? "border-lime-500 bg-[#0f172a]" : "border-[#334155] bg-[#0f172a]/50 opacity-60 hover:opacity-80"}`}
            data-testid="theme-select-light"
          >
            <div className="w-full h-16 bg-white rounded-md border border-gray-200 mb-2 flex items-center justify-center">
              <div className="w-8 h-2 rounded" style={{ backgroundColor: primaryColor }}></div>
            </div>
            <p className="text-xs text-center text-slate-300 font-medium">Light mode</p>
          </button>
        </div>
      </div>

      {/* Color presets */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-5">
        <h3 className="text-sm font-bold text-[#F8FAFC] mb-1">Màu chủ đạo</h3>
        <p className="text-xs text-slate-500 mb-4">Chọn màu chính cho giao diện. Áp dụng cho cả Dark & Light mode.</p>

        <div className="grid grid-cols-4 sm:grid-cols-8 gap-3 mb-5">
          {COLOR_PRESETS.map(preset => (
            <button
              key={preset.hex}
              onClick={() => handlePreset(preset)}
              className={`group flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all ${primaryColor === preset.hex ? "border-white/50 bg-white/5 scale-105" : "border-transparent hover:border-[#334155] hover:bg-[#334155]/30"}`}
              data-testid={`color-preset-${preset.hex.slice(1)}`}
              title={preset.name}
            >
              <div className="relative">
                <div
                  className="w-9 h-9 rounded-full shadow-lg transition-transform group-hover:scale-110"
                  style={{ backgroundColor: preset.hex }}
                />
                {primaryColor === preset.hex && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg className="w-4 h-4 text-white drop-shadow" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  </div>
                )}
              </div>
              <span className="text-[10px] text-slate-400 leading-tight text-center">{preset.name}</span>
            </button>
          ))}
        </div>

        {/* Current color indicator */}
        <div className="flex items-center gap-3 p-3 bg-[#0f172a] rounded-lg border border-[#334155]">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full border border-white/20" style={{ backgroundColor: primaryColor }}></div>
            <div>
              <span className="text-xs text-slate-400">Light:</span>
              <code className="text-xs text-sky-400 ml-1 font-mono">{primaryColor}</code>
            </div>
          </div>
          <div className="w-px h-6 bg-[#334155]"></div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full border border-white/20" style={{ backgroundColor: primaryColorDark }}></div>
            <div>
              <span className="text-xs text-slate-400">Dark:</span>
              <code className="text-xs text-sky-400 ml-1 font-mono">{primaryColorDark}</code>
            </div>
          </div>
          {activePreset && (
            <Badge className="bg-lime-500/10 text-lime-400 border-lime-500/20 text-[10px] px-2 py-0 border ml-auto">{activePreset.name}</Badge>
          )}
        </div>
      </div>

      {/* Custom color input */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-5">
        <h3 className="text-sm font-bold text-[#F8FAFC] mb-1">Màu tuỳ chỉnh</h3>
        <p className="text-xs text-slate-500 mb-4">Nhập mã màu HEX cho Light mode và Dark mode.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-slate-400 text-xs">Màu Light mode</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={customHex}
                onChange={e => setCustomHex(e.target.value)}
                className="w-9 h-9 rounded cursor-pointer border border-[#334155] bg-transparent p-0.5"
                data-testid="custom-color-light"
              />
              <Input
                value={customHex}
                onChange={e => setCustomHex(e.target.value)}
                placeholder="#605dff"
                className="bg-[#0f172a] border-[#334155] text-slate-200 font-mono text-sm flex-1"
                data-testid="custom-hex-light"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-400 text-xs">Màu Dark mode</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={customDarkHex}
                onChange={e => setCustomDarkHex(e.target.value)}
                className="w-9 h-9 rounded cursor-pointer border border-[#334155] bg-transparent p-0.5"
                data-testid="custom-color-dark"
              />
              <Input
                value={customDarkHex}
                onChange={e => setCustomDarkHex(e.target.value)}
                placeholder="#a78bfa"
                className="bg-[#0f172a] border-[#334155] text-slate-200 font-mono text-sm flex-1"
                data-testid="custom-hex-dark"
              />
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Button onClick={handleCustomApply} className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold gap-1.5 text-xs" data-testid="apply-custom-color-btn">
            <Zap className="w-3.5 h-3.5" /> Áp dụng màu tuỳ chỉnh
          </Button>
          <Button variant="outline" onClick={() => { setColor("#605dff", "#a78bfa"); setCustomHex("#605dff"); setCustomDarkHex("#a78bfa"); toast.success("Đã đặt lại về mặc định"); }}
            className="border-[#334155] text-slate-400 hover:text-slate-200 text-xs" data-testid="reset-color-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Mặc định
          </Button>
        </div>
      </div>

      {/* Live preview */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-5">
        <h3 className="text-sm font-bold text-[#F8FAFC] mb-3">Xem trước</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Dark preview */}
          <div className="bg-[#0f172a] rounded-lg p-4 border border-[#334155] space-y-3">
            <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Dark mode</p>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: primaryColorDark }}>
                <Zap className="w-4 h-4 text-[#0f172a]" />
              </div>
              <span className="text-sm font-bold" style={{ color: primaryColorDark }}>KARINOX Arena</span>
            </div>
            <div className="flex gap-2">
              <button className="px-3 py-1.5 rounded-md text-[11px] font-semibold text-[#0f172a]" style={{ backgroundColor: primaryColorDark }}>Nút chính</button>
              <button className="px-3 py-1.5 rounded-md text-[11px] font-medium border border-[#334155]" style={{ color: primaryColorDark }}>Nút phụ</button>
            </div>
            <div className="flex gap-2">
              <Badge className="text-[10px] px-2 py-0 border" style={{ backgroundColor: `${primaryColorDark}15`, color: primaryColorDark, borderColor: `${primaryColorDark}30` }}>Badge 1</Badge>
              <Badge className="text-[10px] px-2 py-0 border" style={{ backgroundColor: `${primaryColorDark}15`, color: primaryColorDark, borderColor: `${primaryColorDark}30` }}>Badge 2</Badge>
            </div>
          </div>
          {/* Light preview */}
          <div className="bg-white rounded-lg p-4 border border-gray-200 space-y-3">
            <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Light mode</p>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                <Zap className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-bold" style={{ color: primaryColor }}>KARINOX Arena</span>
            </div>
            <div className="flex gap-2">
              <button className="px-3 py-1.5 rounded-md text-[11px] font-semibold text-white" style={{ backgroundColor: primaryColor }}>Nút chính</button>
              <button className="px-3 py-1.5 rounded-md text-[11px] font-medium border" style={{ color: primaryColor, borderColor: `${primaryColor}40` }}>Nút phụ</button>
            </div>
            <div className="flex gap-2">
              <Badge className="text-[10px] px-2 py-0 border" style={{ backgroundColor: `${primaryColor}12`, color: primaryColor, borderColor: `${primaryColor}30` }}>Badge 1</Badge>
              <Badge className="text-[10px] px-2 py-0 border" style={{ backgroundColor: `${primaryColor}12`, color: primaryColor, borderColor: `${primaryColor}30` }}>Badge 2</Badge>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
